import React, { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef } from "react";
import ChartVoiceButton from "./ChartVoiceButton";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries, BaselineSeries, createSeriesMarkers } from "lightweight-charts";

// ═══════════════════════════════════════════════════════════════════════
// INDICATOR MATH — all computed client-side from candle data
// ═══════════════════════════════════════════════════════════════════════
const calcEMA = (candles, period) => {
  const k = 2 / (period + 1); let ema = null;
  return candles.map(c => { ema = ema === null ? c.close : c.close * k + ema * (1 - k); return { time: c.time, value: ema }; });
};
const calcSMA = (candles, p) => candles.slice(p - 1).map((_, i) => ({
  time: candles[i + p - 1].time,
  value: candles.slice(i, i + p).reduce((a, c) => a + c.close, 0) / p,
}));
const calcWMA = (candles, p) => candles.slice(p - 1).map((_, i) => {
  const sl = candles.slice(i, i + p); const denom = (p * (p + 1)) / 2;
  return { time: sl[sl.length - 1].time, value: sl.reduce((a, c, j) => a + c.close * (j + 1), 0) / denom };
});
const calcHMA = (candles, p) => {
  const half = calcWMA(candles, Math.floor(p / 2));
  const full = calcWMA(candles, p);
  const sq   = Math.round(Math.sqrt(p));
  const diff = full.map((f, i) => {
    const h = half.find(h2 => h2.time === f.time);
    return h ? { time: f.time, value: 2 * h.value - f.value } : null;
  }).filter(Boolean);
  return calcWMA(diff, sq);
};
const calcDEMA = (candles, p) => {
  const e1 = calcEMA(candles, p), e2 = calcEMA(e1.map((d, i) => ({ ...candles[i], close: d.value })), p);
  return e1.map((d, i) => ({ time: d.time, value: 2 * d.value - (e2[i]?.value ?? d.value) }));
};
const calcTMA = (candles, p = 20) => {
  const sma1 = calcSMA(candles, p);
  if (sma1.length < p) return [];
  const stage = sma1.map(d => ({ time: d.time, close: d.value }));
  return calcSMA(stage, p);
};
const calcTEMA = (candles, p) => {
  const e1 = calcEMA(candles, p);
  const e2 = calcEMA(e1.map((d, i) => ({ ...candles[i], close: d.value })), p);
  const e3 = calcEMA(e2.map((d, i) => ({ ...candles[i], close: d.value })), p);
  return e1.map((d, i) => ({ time: d.time, value: 3 * d.value - 3 * (e2[i]?.value ?? d.value) + (e3[i]?.value ?? d.value) }));
};
const calcBB = (candles, p = 20, mult = 2) => {
  const mid = [], upper = [], lower = [];
  for (let i = p - 1; i < candles.length; i++) {
    const sl = candles.slice(i - p + 1, i + 1).map(c => c.close);
    const sma = sl.reduce((a, v) => a + v, 0) / p;
    const std = Math.sqrt(sl.reduce((a, v) => a + (v - sma) ** 2, 0) / p);
    mid.push({ time: candles[i].time, value: sma });
    upper.push({ time: candles[i].time, value: sma + mult * std });
    lower.push({ time: candles[i].time, value: sma - mult * std });
  }
  return { mid, upper, lower };
};
const calcKC = (candles, p = 20, mult = 2) => {
  const ema = calcEMA(candles, p);
  const atr = calcATR(candles, p);
  return ema.map((e, i) => {
    const a = atr.find(x => x.time === e.time)?.value ?? 0;
    return { mid: { time: e.time, value: e.value }, upper: { time: e.time, value: e.value + mult * a }, lower: { time: e.time, value: e.value - mult * a } };
  });
};
const calcVWAP = (candles) => {
  let cpv = 0, cv = 0;
  return candles.map(c => { const v = c.volume || 1; cpv += ((c.high + c.low + c.close) / 3) * v; cv += v; return { time: c.time, value: cpv / cv }; });
};
const calcATR = (candles, p = 14) => {
  const tr = candles.map((c, i) => i === 0 ? c.high - c.low : Math.max(c.high - c.low, Math.abs(c.high - candles[i-1].close), Math.abs(c.low - candles[i-1].close)));
  let atr = tr.slice(0, p).reduce((a, v) => a + v, 0) / p;
  return candles.slice(p - 1).map((c, i) => {
    if (i > 0) atr = (atr * (p - 1) + tr[i + p - 1]) / p;
    return { time: c.time, value: atr };
  });
};
const calcTMABands = (candles, p = 20, mult = 2) => {
  const tma = calcTMA(candles, p);
  const atr = calcATR(candles, p);
  const atrMap = Object.fromEntries(atr.map(x => [x.time, x.value]));
  const mid = [], upper = [], lower = [];
  for (const t of tma) {
    const a = atrMap[t.time] ?? 0;
    mid.push({ time: t.time, value: t.value });
    upper.push({ time: t.time, value: t.value + mult * a });
    lower.push({ time: t.time, value: t.value - mult * a });
  }
  return { mid, upper, lower };
};
const calcRSI = (candles, p = 14) => {
  let ag = 0, al = 0; const out = [];
  for (let i = 1; i < candles.length; i++) {
    const d = candles[i].close - candles[i-1].close, g = d > 0 ? d : 0, l = d < 0 ? -d : 0;
    if (i <= p) { ag += g / p; al += l / p; if (i === p) out.push({ time: candles[i].time, value: 100 - 100 / (1 + ag / (al || 1e-9)) }); }
    else { ag = (ag * (p-1) + g) / p; al = (al * (p-1) + l) / p; out.push({ time: candles[i].time, value: 100 - 100 / (1 + ag / (al || 1e-9)) }); }
  }
  return out;
};
const calcStoch = (candles, k = 14, d = 3) => {
  const kLine = candles.slice(k - 1).map((c, i) => {
    const sl = candles.slice(i, i + k);
    const lo = Math.min(...sl.map(x => x.low)), hi = Math.max(...sl.map(x => x.high));
    return { time: c.time, value: hi === lo ? 50 : ((c.close - lo) / (hi - lo)) * 100 };
  });
  const dLine = kLine.slice(d - 1).map((_, i) => ({
    time: kLine[i + d - 1].time, value: kLine.slice(i, i + d).reduce((a, v) => a + v.value, 0) / d,
  }));
  return { k: kLine, d: dLine };
};
const calcMACD = (candles, fast = 12, slow = 26, sig = 9) => {
  const ef = calcEMA(candles, fast), es = calcEMA(candles, slow);
  const esMap = Object.fromEntries(es.map(x => [x.time, x.value]));
  const macd = ef.map(f => ({ time: f.time, value: f.value - (esMap[f.time] ?? f.value) }));
  const k = 2 / (sig + 1); let sv = null;
  const signal = macd.map(m => { sv = sv === null ? m.value : m.value * k + sv * (1 - k); return { time: m.time, value: sv }; });
  const hist = macd.map((m, i) => ({ time: m.time, value: m.value - signal[i].value }));
  return { macd, signal, hist };
};
const calcCCI = (candles, p = 20) => candles.slice(p - 1).map((_, i) => {
  const sl = candles.slice(i, i + p);
  const tp = sl.map(c => (c.high + c.low + c.close) / 3);
  const avg = tp.reduce((a, v) => a + v, 0) / p;
  const md = tp.reduce((a, v) => a + Math.abs(v - avg), 0) / p;
  return { time: sl[sl.length - 1].time, value: (tp[tp.length - 1] - avg) / (0.015 * (md || 1e-9)) };
});
const calcWilliamsR = (candles, p = 14) => candles.slice(p - 1).map((c, i) => {
  const sl = candles.slice(i, i + p);
  const hi = Math.max(...sl.map(x => x.high)), lo = Math.min(...sl.map(x => x.low));
  return { time: c.time, value: hi === lo ? -50 : ((hi - c.close) / (hi - lo)) * -100 };
});
const calcMFI = (candles, p = 14) => {
  const tp = candles.map(c => ({ time: c.time, tp: (c.high + c.low + c.close) / 3, mf: ((c.high + c.low + c.close) / 3) * (c.volume || 1), prev: 0 }));
  for (let i = 1; i < tp.length; i++) tp[i].prev = tp[i-1].tp;
  const out = [];
  for (let i = p; i < tp.length; i++) {
    const sl = tp.slice(i - p + 1, i + 1);
    const pmf = sl.filter((x, j) => j > 0 && x.tp >= sl[j-1].tp).reduce((a, x) => a + x.mf, 0);
    const nmf = sl.filter((x, j) => j > 0 && x.tp < sl[j-1].tp).reduce((a, x) => a + x.mf, 0);
    out.push({ time: tp[i].time, value: 100 - 100 / (1 + pmf / (nmf || 1e-9)) });
  }
  return out;
};
const calcOBV = (candles) => {
  let obv = 0;
  return candles.map((c, i) => {
    if (i > 0) obv += c.close > candles[i-1].close ? (c.volume || 1) : c.close < candles[i-1].close ? -(c.volume || 1) : 0;
    return { time: c.time, value: obv };
  });
};
const calcCMF = (candles, p = 20) => {
  const mfv = candles.map(c => { const r = c.high - c.low; return { time: c.time, mfv: r ? ((c.close - c.low) - (c.high - c.close)) / r * (c.volume || 1) : 0, vol: c.volume || 1 }; });
  return mfv.slice(p - 1).map((_, i) => {
    const sl = mfv.slice(i, i + p);
    return { time: sl[sl.length-1].time, value: sl.reduce((a, x) => a + x.mfv, 0) / sl.reduce((a, x) => a + x.vol, 0) };
  });
};
const calcADX = (candles, p = 14) => {
  if (candles.length < p + 1) return [];
  const dm = candles.slice(1).map((c, i) => {
    const ph = c.high - candles[i].high, pl = candles[i].low - c.low;
    return { time: c.time, pDM: ph > pl && ph > 0 ? ph : 0, nDM: pl > ph && pl > 0 ? pl : 0, tr: Math.max(c.high - c.low, Math.abs(c.high - candles[i].close), Math.abs(c.low - candles[i].close)) };
  });
  let sTR = dm.slice(0, p).reduce((a, x) => a + x.tr, 0);
  let spDM = dm.slice(0, p).reduce((a, x) => a + x.pDM, 0);
  let snDM = dm.slice(0, p).reduce((a, x) => a + x.nDM, 0);
  let adxVal = 0; const out = [];
  for (let i = p; i < dm.length; i++) {
    sTR = sTR - sTR / p + dm[i].tr;
    spDM = spDM - spDM / p + dm[i].pDM;
    snDM = snDM - snDM / p + dm[i].nDM;
    const pDI = (spDM / sTR) * 100, nDI = (snDM / sTR) * 100;
    const dx = Math.abs(pDI - nDI) / ((pDI + nDI) || 1e-9) * 100;
    adxVal = i === p ? dx : (adxVal * (p - 1) + dx) / p;
    out.push({ time: dm[i].time, value: adxVal });
  }
  return out;
};
const calcAroon = (candles, p = 25) => {
  const up = [], dn = [];
  for (let i = p; i < candles.length; i++) {
    const sl = candles.slice(i - p, i + 1);
    const hiIdx = sl.reduce((a, c, j) => c.high > sl[a].high ? j : a, 0);
    const loIdx = sl.reduce((a, c, j) => c.low < sl[a].low ? j : a, 0);
    up.push({ time: candles[i].time, value: ((p - (p - hiIdx)) / p) * 100 });
    dn.push({ time: candles[i].time, value: ((p - (p - loIdx)) / p) * 100 });
  }
  return { up, dn };
};
const calcSupertrend = (candles, p = 10, mult = 3) => {
  const atr = calcATR(candles, p);
  const atrMap = Object.fromEntries(atr.map(x => [x.time, x.value]));
  let dir = 1, prevUpper = 0, prevLower = 0, prevClose = candles[0]?.close ?? 0;
  // O(n) loop — no indexOf. Color handled by SEPARATE up/down series at render time (LineSeries ignores per-bar color).
  const out = [];
  for (const c of candles) {
    const a = atrMap[c.time]; if (a == null) { prevClose = c.close; continue; }
    const hl2 = (c.high + c.low) / 2;
    let upper = hl2 + mult * a, lower = hl2 - mult * a;
    if (prevLower) lower = (lower > prevLower || prevClose < prevLower) ? lower : prevLower;
    if (prevUpper) upper = (upper < prevUpper || prevClose > prevUpper) ? upper : prevUpper;
    if (dir === 1 && c.close < lower) dir = -1;
    else if (dir === -1 && c.close > upper) dir = 1;
    out.push({ time: c.time, value: dir === 1 ? lower : upper });
    prevUpper = upper; prevLower = lower; prevClose = c.close;
  }
  return out;
};
const calcParabolicSAR = (candles, step = 0.02, max = 0.2) => {
  if (candles.length < 3) return [];
  let bull = true, sar = candles[0].low, ep = candles[0].high, af = step; const out = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i], prev = candles[i - 1];
    const prev2 = i >= 2 ? candles[i - 2] : prev;
    sar = sar + af * (ep - sar);
    if (bull) {
      if (c.low < sar) { bull = false; sar = ep; ep = c.low; af = step; }
      else { if (c.high > ep) { ep = c.high; af = Math.min(af + step, max); } sar = Math.min(sar, prev.low, prev2.low); }
    } else {
      if (c.high > sar) { bull = true; sar = ep; ep = c.high; af = step; }
      else { if (c.low < ep) { ep = c.low; af = Math.min(af + step, max); } sar = Math.max(sar, prev.high, prev2.high); }
    }
    if (!isFinite(sar)) continue; // skip NaN/Infinity
    out.push({ time: c.time, value: sar });
  }
  return out;
};
const calcDonchian = (candles, p = 20) => {
  const upper = [], lower = [], mid = [];
  for (let i = p - 1; i < candles.length; i++) {
    const sl = candles.slice(i - p + 1, i + 1);
    const hi = Math.max(...sl.map(c => c.high)), lo = Math.min(...sl.map(c => c.low));
    upper.push({ time: candles[i].time, value: hi });
    lower.push({ time: candles[i].time, value: lo });
    mid.push({ time: candles[i].time, value: (hi + lo) / 2 });
  }
  return { upper, lower, mid };
};

// TMA + CG (Centered Gaussian) bands — TMA over 2*halfLength bars, ±bandsDeviations × MAD.
const calcTMACG = (candles, halfLength = 55, bandsDeviations = 2.5) => {
  if (candles.length < halfLength * 2) return { mid: [], upper: [], lower: [] };
  const p = halfLength * 2;
  const tma = calcTMA(candles, p);
  const tmaMap = Object.fromEntries(tma.map(x => [x.time, x.value]));
  const mid = [], upper = [], lower = [];
  for (let i = p - 1; i < candles.length; i++) {
    const slice = candles.slice(i - p + 1, i + 1);
    const center = tmaMap[candles[i].time];
    if (center == null) continue;
    const dev = slice.reduce((a, c) => a + Math.abs(c.close - center), 0) / p;
    mid.push({ time: candles[i].time, value: center });
    upper.push({ time: candles[i].time, value: center + bandsDeviations * dev });
    lower.push({ time: candles[i].time, value: center - bandsDeviations * dev });
  }
  return { mid, upper, lower };
};

const calcIchimoku = (candles) => {
  const high = (sl) => Math.max(...sl.map(c => c.high)), low = (sl) => Math.min(...sl.map(c => c.low));
  const tenkan = [], kijun = [], senkouA = [], senkouB = [], chikou = [];
  // Standard Ichimoku periods (TradingView default): Tenkan 9, Kijun 26, Senkou B 52, displacement 26.
  const T = 9, K = 26, S = 52, D = 26;
  for (let i = 0; i < candles.length; i++) {
    if (i >= T - 1) tenkan.push({ time: candles[i].time, value: (high(candles.slice(i - T + 1, i + 1)) + low(candles.slice(i - T + 1, i + 1))) / 2 });
    if (i >= K - 1) kijun.push({ time: candles[i].time, value: (high(candles.slice(i - K + 1, i + 1)) + low(candles.slice(i - K + 1, i + 1))) / 2 });
    // Senkou A = (Tenkan + Kijun) / 2 plotted D periods into the future
    if (i >= K - 1 && i + D < candles.length) {
      const t = (Array.isArray(tenkan) && tenkan.length ? tenkan[tenkan.length-1]?.value : undefined); const k = (Array.isArray(kijun) && kijun.length ? kijun[kijun.length-1]?.value : undefined);
      if (t != null && k != null) senkouA.push({ time: candles[i + D].time, value: (t + k) / 2 });
    }
    // Senkou B plotted D periods into the future
    if (i >= S - 1 && i + D < candles.length) senkouB.push({ time: candles[i + D].time, value: (high(candles.slice(i - S + 1, i + 1)) + low(candles.slice(i - S + 1, i + 1))) / 2 });
    // Chikou = current close plotted D periods BACK (NOT forward!)
    if (i >= D) chikou.push({ time: candles[i - D].time, value: candles[i].close });
  }
  return { tenkan, kijun, senkouA, senkouB, chikou };
};
const calcROC = (candles, p = 12) => candles.slice(p).map((c, i) => ({ time: c.time, value: ((c.close - candles[i].close) / candles[i].close) * 100 }));
const calcMomentum = (candles, p = 10) => candles.slice(p).map((c, i) => ({ time: c.time, value: c.close - candles[i].close }));
const calcVolume = (candles) => candles.map(c => {
  // Color bullish bars green, bearish red. open may be undefined for tick data → fall back to close vs prev.
  const isBull = c.open != null ? c.close >= c.open : (c.close >= (c.prevClose ?? c.close));
  return { time: c.time, value: c.volume || 1, color: isBull ? "#00d4aa66" : "#ef444466" };
});
const calcHeikinAshi = (candles) => {
  if (!candles.length) return [];
  const out = [];
  let haOpen = candles[0].open ?? candles[0].close;
  let haClose = (candles[0].open + candles[0].high + candles[0].low + candles[0].close) / 4;
  let haHigh = Math.max(candles[0].high, haOpen, haClose);
  let haLow = Math.min(candles[0].low, haOpen, haClose);
  out.push({ time: candles[0].time, open: haOpen, high: haHigh, low: haLow, close: haClose });
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i];
    const close = (c.open + c.high + c.low + c.close) / 4;
    const open = (haOpen + haClose) / 2;
    const high = Math.max(c.high, open, close);
    const low = Math.min(c.low, open, close);
    out.push({ time: c.time, open, high, low, close });
    haOpen = open;
    haClose = close;
  }
  return out;
};

// ── REA REVERSE-ENGINEERED POPULAR INDICATORS ────────────────────────
const calcStochRSI = (candles, rsiP = 14, stochP = 14, kSmooth = 3, dSmooth = 3) => {
  const rsi = calcRSI(candles, rsiP);
  if (!rsi || rsi.length < stochP) return { k: [], d: [] };
  const rawStoch = [];
  for (let i = stochP - 1; i < rsi.length; i++) {
    const sl = rsi.slice(i - stochP + 1, i + 1).map(x => x.value);
    const minR = Math.min(...sl), maxR = Math.max(...sl);
    const val = maxR === minR ? 50 : ((rsi[i].value - minR) / (maxR - minR || 1e-9)) * 100;
    rawStoch.push({ time: rsi[i].time, value: Math.max(0, Math.min(100, val)) });
  }
  const kLine = rawStoch.slice(kSmooth - 1).map((_, i) => ({
    time: rawStoch[i + kSmooth - 1].time,
    value: rawStoch.slice(i, i + kSmooth).reduce((a, v) => a + v.value, 0) / kSmooth,
  }));
  const dLine = kLine.slice(dSmooth - 1).map((_, i) => ({
    time: kLine[i + dSmooth - 1].time,
    value: kLine.slice(i, i + dSmooth).reduce((a, v) => a + v.value, 0) / dSmooth,
  }));
  return { k: kLine, d: dLine };
};

const calcVWMA = (candles, p = 20) => {
  if (candles.length < p) return [];
  return candles.slice(p - 1).map((_, i) => {
    const sl = candles.slice(i, i + p);
    let sumPV = 0, sumV = 0;
    for (const c of sl) {
      const v = c.volume || 1;
      sumPV += c.close * v;
      sumV += v;
    }
    return { time: sl[sl.length - 1].time, value: sumPV / (sumV || 1) };
  });
};

const calcPivotsClassic = (candles) => {
  if (!candles || candles.length < 2) return { pp: [], r1: [], r2: [], r3: [], s1: [], s2: [], s3: [] };
  const lookback = Math.min(candles.length, 30);
  const sl = candles.slice(candles.length - lookback, candles.length - 1);
  const H = Math.max(...sl.map(c => c.high)), L = Math.min(...sl.map(c => c.low)), C = sl[sl.length - 1].close;
  const R = H - L;
  const ppVal = (H + L + C) / 3;
  const r1Val = 2 * ppVal - L;
  const r2Val = ppVal + R;
  const r3Val = H + 2 * (ppVal - L);
  const s1Val = 2 * ppVal - H;
  const s2Val = ppVal - R;
  const s3Val = L - 2 * (H - ppVal);
  const recent = candles.slice(-lookback);
  return {
    pp: recent.map(c => ({ time: c.time, value: ppVal })),
    r1: recent.map(c => ({ time: c.time, value: r1Val })),
    r2: recent.map(c => ({ time: c.time, value: r2Val })),
    r3: recent.map(c => ({ time: c.time, value: r3Val })),
    s1: recent.map(c => ({ time: c.time, value: s1Val })),
    s2: recent.map(c => ({ time: c.time, value: s2Val })),
    s3: recent.map(c => ({ time: c.time, value: s3Val })),
  };
};

const calcPivotsCamarilla = (candles) => {
  if (!candles || candles.length < 2) return { h4: [], h3: [], l3: [], l4: [] };
  const lookback = Math.min(candles.length, 30);
  const sl = candles.slice(candles.length - lookback, candles.length - 1);
  const H = Math.max(...sl.map(c => c.high)), L = Math.min(...sl.map(c => c.low)), C = sl[sl.length - 1].close;
  const R = H - L;
  const h4Val = C + R * 0.55;
  const h3Val = C + R * 0.275;
  const l3Val = C - R * 0.275;
  const l4Val = C - R * 0.55;
  const recent = candles.slice(-lookback);
  return {
    h4: recent.map(c => ({ time: c.time, value: h4Val })),
    h3: recent.map(c => ({ time: c.time, value: h3Val })),
    l3: recent.map(c => ({ time: c.time, value: l3Val })),
    l4: recent.map(c => ({ time: c.time, value: l4Val })),
  };
};

const calcZigZag = (candles, depth = 12, deviation = 5, backstep = 3) => {
  if (!candles || candles.length < depth) return [];
  const n = candles.length;
  const swings = [];
  let lastHighIdx = -1, lastLowIdx = -1, state = 0; // 1 = peak, -1 = trough
  for (let i = depth - 1; i < n; i++) {
    const sl = candles.slice(i - depth + 1, i + 1);
    const hi = Math.max(...sl.map(c => c.high)), lo = Math.min(...sl.map(c => c.low));
    const isHigh = candles[i].high === hi;
    const isLow = candles[i].low === lo;
    if (!isHigh && !isLow) continue;
    if (isHigh) {
      if (state === 1) {
        if (swings.length && candles[i].high > swings[swings.length - 1].value) {
          swings[swings.length - 1] = { time: candles[i].time, value: candles[i].high };
          lastHighIdx = i;
        }
      } else if (lastLowIdx === -1 || (i - lastLowIdx) >= backstep) {
        swings.push({ time: candles[i].time, value: candles[i].high });
        lastHighIdx = i;
        state = 1;
      }
    }
    if (isLow) {
      if (state === -1) {
        if (swings.length && candles[i].low < swings[swings.length - 1].value) {
          swings[swings.length - 1] = { time: candles[i].time, value: candles[i].low };
          lastLowIdx = i;
        }
      } else if (lastHighIdx === -1 || (i - lastHighIdx) >= backstep) {
        swings.push({ time: candles[i].time, value: candles[i].low });
        lastLowIdx = i;
        state = -1;
      }
    }
  }
  return swings;
};

const calcChandelierExit = (candles, period = 22, mult = 3) => {
  if (!candles || candles.length < period) return { longStop: [], shortStop: [] };
  const atr = calcATR(candles, period);
  const atrMap = Object.fromEntries(atr.map(x => [x.time, x.value]));
  const longStop = [];
  const shortStop = [];
  let dir = 1;
  let prevLong = 0, prevShort = 0;

  for (let i = period - 1; i < candles.length; i++) {
    const c = candles[i];
    const a = atrMap[c.time];
    if (a == null) continue;
    const window = candles.slice(i - period + 1, i + 1);
    const highestHigh = Math.max(...window.map(x => x.high));
    const lowestLow = Math.min(...window.map(x => x.low));

    let curLong = highestHigh - mult * a;
    let curShort = lowestLow + mult * a;

    if (prevLong && candles[i - 1].close > prevLong) {
      curLong = Math.max(curLong, prevLong);
    }
    if (prevShort && candles[i - 1].close < prevShort) {
      curShort = Math.min(curShort, prevShort);
    }

    if (dir === 1 && c.close < curLong) {
      dir = -1;
    } else if (dir === -1 && c.close > curShort) {
      dir = 1;
    }

    if (dir === 1) {
      longStop.push({ time: c.time, value: curLong });
    } else {
      shortStop.push({ time: c.time, value: curShort });
    }
    prevLong = curLong;
    prevShort = curShort;
  }
  return { longStop, shortStop };
};

const calcChoppinessIndex = (candles, period = 14) => {
  if (!candles || candles.length <= period) return [];
  const tr1 = candles.map((c, i) => {
    if (i === 0) return c.high - c.low;
    const prevClose = candles[i - 1].close;
    return Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose));
  });

  const out = [];
  const log10n = Math.log10(period);

  for (let i = period; i < candles.length; i++) {
    const slCandles = candles.slice(i - period + 1, i + 1);
    const slTR = tr1.slice(i - period + 1, i + 1);
    const sumTR = slTR.reduce((acc, v) => acc + v, 0);
    const maxH = Math.max(...slCandles.map(c => c.high));
    const minL = Math.min(...slCandles.map(c => c.low));
    const range = maxH - minL;
    if (range <= 0) {
      out.push({ time: candles[i].time, value: 50 });
      continue;
    }
    const chop = 100 * (Math.log10(sumTR / range) / log10n);
    out.push({ time: candles[i].time, value: Math.max(0, Math.min(100, chop)) });
  }
  return out;
};

// ═══════════════════════════════════════════════════════════════════════
// CUSTOM INDICATOR FORMULA EVALUATOR
// Supports: ema(src,p), sma(src,p), wma(src,p), rsi(src,p), atr(p),
//           bb_upper(p,mult), bb_lower(p,mult), macd_line(f,s), close, open, high, low
// Returns array of {time, value} points for the chart.
// ═══════════════════════════════════════════════════════════════════════
function evalCustomFormula(formula, candles) {
  if (!formula || !candles.length) return [];
  try {
    const closes = candles.map(c => c.close);
    const opens  = candles.map(c => c.open);
    const highs  = candles.map(c => c.high);
    const lows   = candles.map(c => c.low);

    // Build a safe evaluation context
    const ctx = {
      ema:       (src, p) => { const arr = typeof src === "string" ? (src==="close"?candles:candles.map(c=>c[src])) : candles; return calcEMA(arr.map ? arr : candles, p||src); },
      sma:       (src, p) => calcSMA(candles, p),
      wma:       (src, p) => calcWMA(candles, p),
      rsi:       (src, p) => calcRSI(candles, p||14),
      atr:       (p)      => calcATR(candles, p||14),
      macd_line: (f,s)    => calcMACD(candles, f||12, s||26, 9).macd,
      bb_upper:  (p,m)    => calcBB(candles, p||20, m||2).upper,
      bb_mid:    (p,m)    => calcBB(candles, p||20, m||2).mid,
      bb_lower:  (p,m)    => calcBB(candles, p||20, m||2).lower,
      vwap:      ()       => calcVWAP(candles),
    };

    // Map common formula strings to indicator functions
    const f = formula.trim().toLowerCase().replace(/\s+/g,"");

    // ema(N) or ema(close,N)
    const emaM = f.match(/^ema\((?:close,)?(\d+)\)$/);
    if (emaM) return calcEMA(candles, parseInt(emaM[1]));

    const smaM = f.match(/^sma\((?:close,)?(\d+)\)$/);
    if (smaM) return calcSMA(candles, parseInt(smaM[1]));

    const wmaM = f.match(/^wma\((?:close,)?(\d+)\)$/);
    if (wmaM) return calcWMA(candles, parseInt(wmaM[1]));

    const rsiM = f.match(/^rsi\((?:close,)?(\d+)\)$/);
    if (rsiM) return calcRSI(candles, parseInt(rsiM[1]));

    const atrM = f.match(/^atr\((\d+)\)$/);
    if (atrM) return calcATR(candles, parseInt(atrM[1]));

    const hmaM = f.match(/^hma\((?:close,)?(\d+)\)$/);
    if (hmaM) return calcHMA(candles, parseInt(hmaM[1]));

    const demaM = f.match(/^dema\((?:close,)?(\d+)\)$/);
    if (demaM) return calcDEMA(candles, parseInt(demaM[1]));

    const temaM = f.match(/^tema\((?:close,)?(\d+)\)$/);
    if (temaM) return calcTEMA(candles, parseInt(temaM[1]));

    // bb_upper(p,mult) | bb_lower(p,mult) | bb_mid(p,mult)
    const bbUM = f.match(/^bb_(upper|lower|mid)\((\d+)(?:,(\d+(?:\.\d+)?))?\)$/);
    if (bbUM) {
      const p = parseInt(bbUM[2]), m = parseFloat(bbUM[3]||"2");
      return calcBB(candles, p, m)[bbUM[1]];
    }

    // vwap()
    if (f === "vwap()") return calcVWAP(candles);

    // macd(fast,slow) — returns macd line
    const macdM = f.match(/^macd\((\d+),(\d+)\)$/);
    if (macdM) return calcMACD(candles, parseInt(macdM[1]), parseInt(macdM[2]), 9).macd;

    return [];
  } catch {
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════
// INDICATOR CATALOGUE — all free indicators, grouped by category
// ═══════════════════════════════════════════════════════════════════════
// VOLUME PROFILE — price level histogram from candle data
// ═══════════════════════════════════════════════════════════════════════
const calcVolumeProfile = (candlesInput, bins = 24) => {
  const candles = Array.isArray(candlesInput) ? candlesInput : candlesInput?.candles;
  if (!candles || candles.length < 2) return [];
  const highs = candles.map(c => Number(c.high)).filter(isFinite);
  const lows = candles.map(c => Number(c.low)).filter(isFinite);
  if (!highs.length || !lows.length) return [];
  const minP = Math.min(...lows), maxP = Math.max(...highs);
  if (maxP <= minP || !isFinite(minP) || !isFinite(maxP)) return [];
  const step = (maxP - minP) / bins;
  const buckets = Array.from({ length: bins }, (_, i) => ({
    price: minP + step * (i + 0.5),
    volume: 0,
  }));
  candles.forEach(c => {
    const vol = Number(c.volume) || 1;
    const candleRange = (Number(c.high) - Number(c.low)) || step;
    buckets.forEach(b => {
      const overlap = Math.max(0, Math.min(Number(c.high), b.price + step / 2) - Math.max(Number(c.low), b.price - step / 2));
      if (overlap > 0) b.volume += vol * (overlap / candleRange);
    });
  });
  const res = buckets.filter(b => b.volume > 0);
  if (res.length === 0) return [];
  let maxB = res[0];
  let totalVol = 0;
  for (const b of res) {
    totalVol += b.volume;
    if (b.volume > maxB.volume) maxB = b;
  }
  res.poc = maxB.price;
  const sorted = [...res].sort((a, b) => b.volume - a.volume);
  let vaVol = 0;
  const inVA = [];
  for (const b of sorted) {
    inVA.push(b.price);
    vaVol += b.volume;
    if (vaVol >= totalVol * 0.70) break;
  }
  res.vah = inVA.length ? Math.max(...inVA) : maxB.price;
  res.val = inVA.length ? Math.min(...inVA) : maxB.price;
  return res;
};

const INDICATOR_CATALOGUE = [
  // ── Moving Averages ──────────────────────────────────────────────────
  { id:"ema9",   cat:"Moving Averages", label:"EMA 9",       type:"ema",   params:{period:9},   color:"#f59e0b", pane:"main" },
  { id:"ema20",  cat:"Moving Averages", label:"EMA 20",      type:"ema",   params:{period:20},  color:"#3b82f6", pane:"main" },
  { id:"ema50",  cat:"Moving Averages", label:"EMA 50",      type:"ema",   params:{period:50},  color:"#a78bfa", pane:"main" },
  { id:"ema100", cat:"Moving Averages", label:"EMA 100",     type:"ema",   params:{period:100}, color:"#f87171", pane:"main" },
  { id:"ema200", cat:"Moving Averages", label:"EMA 200",     type:"ema",   params:{period:200}, color:"#fb923c", pane:"main" },
  { id:"sma9",   cat:"Moving Averages", label:"SMA 9",       type:"sma",   params:{period:9},   color:"#fde68a", pane:"main" },
  { id:"sma20",  cat:"Moving Averages", label:"SMA 20",      type:"sma",   params:{period:20},  color:"#34d399", pane:"main" },
  { id:"sma50",  cat:"Moving Averages", label:"SMA 50",      type:"sma",   params:{period:50},  color:"#6ee7b7", pane:"main" },
  { id:"sma200", cat:"Moving Averages", label:"SMA 200",     type:"sma",   params:{period:200}, color:"#a7f3d0", pane:"main" },
  { id:"wma",    cat:"Moving Averages", label:"WMA 20",      type:"wma",   params:{period:20},  color:"#67e8f9", pane:"main" },
  { id:"hma",    cat:"Moving Averages", label:"HMA 20",      type:"hma",   params:{period:20},  color:"#c4b5fd", pane:"main" },
  { id:"dema",   cat:"Moving Averages", label:"DEMA 20",     type:"dema",  params:{period:20},  color:"#f9a8d4", pane:"main" },
  { id:"tema",   cat:"Moving Averages", label:"TEMA 20",     type:"tema",  params:{period:20},  color:"#fca5a5", pane:"main" },
  { id:"vwap",   cat:"Moving Averages", label:"VWAP",        type:"vwap",  params:{},           color:"#22d3ee", pane:"main" },
  { id:"vwma",   cat:"Moving Averages", label:"VWMA 20",     type:"vwma",  params:{period:20},  color:"#818cf8", pane:"main" },
  { id:"tma",    cat:"Moving Averages", label:"TMA 20",      type:"tma",   params:{period:20},  color:"#38bdf8", pane:"main" },
  // ── Support & Resistance ──────────────────────────────────────────
  { id:"pivot_classic", cat:"Support & Resistance", label:"Pivot Points (Floor)", type:"pivot_classic", params:{}, color:"#eab308", pane:"main" },
  { id:"pivot_camarilla", cat:"Support & Resistance", label:"Camarilla Pivots", type:"pivot_camarilla", params:{}, color:"#06b6d4", pane:"main" },
  // ── Bands & Channels ────────────────────────────────────────────────
  { id:"bb",     cat:"Bands & Channels", label:"Bollinger Bands", type:"bb", params:{period:20,mult:2}, color:"#64748b", pane:"main" },
  { id:"kc",     cat:"Bands & Channels", label:"Keltner Channel", type:"kc", params:{period:20,mult:2}, color:"#475569", pane:"main" },
  { id:"donchian",cat:"Bands & Channels",label:"Donchian Channel",type:"donchian",params:{period:20},   color:"#1d4ed8", pane:"main" },
  { id:"tmabands",cat:"Bands & Channels",label:"TMA Bands",      type:"tmabands",params:{period:20,mult:2}, color:"#0ea5e9", pane:"main" },
  { id:"tmacg",cat:"Bands & Channels",label:"TMA+CG",type:"tmacg",params:{halfLength:55,bandsDeviations:2.5}, color:"#9333ea", pane:"main" },
  // ── Oscillators ─────────────────────────────────────────────────────
  { id:"rsi",    cat:"Oscillators", label:"RSI 14",         type:"rsi",   params:{period:14},  color:"#f59e0b", pane:"sub" },
  { id:"stoch",  cat:"Oscillators", label:"Stochastic",     type:"stoch", params:{k:14,d:3},   color:"#3b82f6", pane:"sub" },
  { id:"stochrsi", cat:"Oscillators", label:"Stochastic RSI", type:"stochrsi", params:{rsi:14,stoch:14,k:3,d:3}, color:"#38bdf8", pane:"sub" },
  { id:"chop",   cat:"Oscillators", label:"Choppiness Index", type:"chop", params:{period:14}, color:"#e879f9", pane:"sub" },
  { id:"cci",    cat:"Oscillators", label:"CCI 20",         type:"cci",   params:{period:20},  color:"#a78bfa", pane:"sub" },
  { id:"mfi",    cat:"Oscillators", label:"MFI 14",         type:"mfi",   params:{period:14},  color:"#34d399", pane:"sub" },
  { id:"willr",  cat:"Oscillators", label:"Williams %R",    type:"willr", params:{period:14},  color:"#f87171", pane:"sub" },
  { id:"roc",    cat:"Oscillators", label:"ROC 12",         type:"roc",   params:{period:12},  color:"#67e8f9", pane:"sub" },
  { id:"momentum",cat:"Oscillators",label:"Momentum 10",   type:"momentum",params:{period:10}, color:"#fde68a", pane:"sub" },
  // ── MACD ─────────────────────────────────────────────────────────────
  { id:"macd",   cat:"MACD", label:"MACD (12,26,9)",       type:"macd",  params:{fast:12,slow:26,signal:9}, color:"#00d4aa", pane:"sub" },
  // ── Trend ────────────────────────────────────────────────────────────
  { id:"adx",    cat:"Trend", label:"ADX 14",              type:"adx",   params:{period:14},  color:"#f59e0b", pane:"sub" },
  { id:"aroon",  cat:"Trend", label:"Aroon 25",            type:"aroon", params:{period:25},  color:"#3b82f6", pane:"sub" },
  { id:"zigzag", cat:"Trend", label:"ZigZag (12,5,3)",     type:"zigzag", params:{depth:12,deviation:5,backstep:3}, color:"#f97316", pane:"main" },
  { id:"chandelier", cat:"Trend", label:"Chandelier Exit", type:"chandelier", params:{period:22,mult:3}, color:"#ec4899", pane:"main" },
  { id:"supertrend",cat:"Trend",label:"Supertrend",        type:"supertrend",params:{period:10,mult:3}, color:"#00d4aa", pane:"main" },
  { id:"psar",   cat:"Trend", label:"Parabolic SAR",       type:"psar",  params:{step:0.02,max:0.2},    color:"#f59e0b", pane:"main" },
  { id:"ichimoku",cat:"Trend",label:"Ichimoku Cloud",      type:"ichimoku",params:{},                   color:"#3b82f6", pane:"main" },
  { id:"heikin", cat:"Price", label:"Heikin-Ashi",         type:"heikin", params:{},                    color:"#4ade80", pane:"main" },
  // ── Volatility ───────────────────────────────────────────────────────
  { id:"atr",    cat:"Volatility", label:"ATR 14",         type:"atr",   params:{period:14},  color:"#94a3b8", pane:"sub" },
  // ── Volume ───────────────────────────────────────────────────────────
  { id:"volume", cat:"Volume", label:"Volume",             type:"volume",params:{},           color:"#1e3a5f", pane:"sub" },
  { id:"obv",    cat:"Volume", label:"OBV",                type:"obv",   params:{},           color:"#22d3ee", pane:"sub" },
  { id:"cmf",    cat:"Volume", label:"CMF 20",             type:"cmf",   params:{period:20},  color:"#34d399", pane:"sub" },
];
const CATEGORIES = [...new Set(INDICATOR_CATALOGUE.map(i => i.cat)), "Custom"];

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const ChartWidget = forwardRef(function ChartWidget({ symbol, timeframe, data, height = 400, onBuy, onSell, activeTrades = [], onCloseTrade, stake = 1, onMlPredict, strategyIndicators, onRunAnalyze, onRunFindTrade, onTrade, onCloseSymbol, onCloseAll, onClearIndicators, onSpeak, onSetSymbol, onSetTimeframe, lastPrice = null, signalMarkerRef = null }, ref) {
  const containerRef  = useRef(); const chartRef = useRef(null); const seriesRef = useRef(null);
  const markersPrimitiveRef = useRef(null); // lightweight-charts v5 series markers primitive
  const [signalMarkers, setSignalMarkers] = useState([]); // live strategy signals from AmberScanner
  // P1-2026-08-31: state mirror of chartRef so useImperativeHandle screenshot()
  // can always find the current chart even if React remounts the ChartWidget
  // component underneath our forwardRef wrapper. useState here is intentional
  // (re-render cost is negligible; the chart effect runs once per mount).
  const [chartInstance, setChartInstance] = useState(null);
  const dataRef       = useRef(data);
  // 2026-10-09 agent-ui: preset modal called an undeclared addLog?.() (ReferenceError after saving) — local no-op logger.
  const addLog = (msg) => { try { console.log("[ChartWidget]", msg); } catch {} };
  useEffect(() => { dataRef.current = data; }, [data]);
  const subChartRef   = useRef(null); const wrapperRef = useRef();
  const indicatorRefs = useRef({});
  const customSeriesRefs = useRef([]);
  const tradeLinesRef = useRef({});  // P0.1: {tradeId: [priceLine, ...]} — tracked so we can clear when trade closes

  useEffect(() => {
    if (!signalMarkerRef) return;
    signalMarkerRef.current = (price, direction, timestamp) => {
      try {
        const cur = Array.isArray(dataRef.current) ? dataRef.current : dataRef.current?.candles;
        const lastCandleTime = cur && cur.length ? cur[cur.length - 1].time : Math.floor(Date.now() / 1000);
        const markerTime = timestamp ? Math.floor(Number(timestamp) / 1000) : lastCandleTime;
        const newMarker = {
          time: markerTime,
          position: direction === "CALL" ? "belowBar" : "aboveBar",
          color: direction === "CALL" ? "#00d4aa" : "#ef4444",
          shape: direction === "CALL" ? "arrowUp" : "arrowDown",
          text: `⚡ SIGNAL ${direction}${price ? ` @ ${Number(price).toFixed(2)}` : ""}`,
        };
        setSignalMarkers(prev => [...prev.slice(-20), newMarker]);
      } catch (err) {
        console.warn("[ChartWidget] signalMarker error:", err);
      }
    };
    return () => {
      if (signalMarkerRef) signalMarkerRef.current = null;
    };
  }, [signalMarkerRef]);

  useEffect(() => {
    setSignalMarkers([]);
  }, [symbol]);

  // ── P0 FEATURES — historical trade markers + rectangle drawing + line cleanup ──
  const [historicalTrades, setHistoricalTrades]   = useState([]);   // last 50 closed trades for marker overlay
  const [showHistoryMarkers, setShowHistoryMarkers] = useState(() => { try { return localStorage.getItem("kara_chart_history_markers") !== "false"; } catch { return true; } });
  const [showActiveMarkers, setShowActiveMarkers] = useState(() => { try { return localStorage.getItem("kara_chart_active_markers") !== "false"; } catch { return true; } });
  const historicalTradesRef = useRef([]);                            // mirror for click handler closure
  useEffect(() => { historicalTradesRef.current = historicalTrades; }, [historicalTrades]);
  useEffect(() => { try { localStorage.setItem("kara_chart_history_markers", String(showHistoryMarkers)); } catch {} }, [showHistoryMarkers]);
  useEffect(() => { try { localStorage.setItem("kara_chart_active_markers", String(showActiveMarkers)); } catch {} }, [showActiveMarkers]);
  const lastHistoryFetchRef = useRef({ sym: null, ts: 0 });           // dedupe fetches per symbol

  // ── P1 SESSION OVERLAY + HOUR HEATMAP — pure frontend, zero new backend routes ──
  // Backend /api/analytics/hourly already exists in server.js (returns hour_utc, session, trades, wins, losses, pnl, win_rate_pct).
  const [hourlyStats, setHourlyStats]       = useState({});           // {hour_utc: {trades, wins, pnl, win_rate_pct, session}}
  const [showSessions, setShowSessions]     = useState(() => { try { return localStorage.getItem("kara_chart_sessions") === "true"; } catch { return false; } });
  const [showHourHeatmap, setShowHourHeatmap] = useState(() => { try { return localStorage.getItem("kara_chart_hour_heatmap") !== "false"; } catch { return true; } });
  const [hoveredHour, setHoveredHour]       = useState(null);
  const [crosshairOHLC, setCrosshairOHLC] = useState(null);  // P2 #14 OHLC legend at crosshair position
  // (session marker refs declared near the effect that uses them — sessionMarkersRef)
  const lastHourlyFetchRef                  = useRef({ sym: null, ts: 0 });
    // P2 #17 CROSS-CHART LINK (2026-08-31)
    const [linkMode, setLinkMode] = useState(false);
    const linkBCRef = useRef(null);
    useEffect(() => {
      try {
        linkBCRef.current = new BroadcastChannel("kara_chart_link");
        return () => { try { linkBCRef.current.close(); } catch {} };
      } catch { return () => {}; }
    }, []);
    useEffect(() => {
      const bc = linkBCRef.current; if (!bc) return;
      const handler = (ev) => {
        const m = ev.data; if (!m || m.source === "self") return;
        try {
          const ts = chartRef.current && chartRef.current.timeScale();
          if (ts && typeof m.from === "number" && typeof m.to === "number") {
            ts.setVisibleLogicalRange({ from: m.from, to: m.to });
          }
        } catch {}
      };
      bc.addEventListener("message", handler);
      return () => bc.removeEventListener("message", handler);
    }, []);
  useEffect(() => { try { localStorage.setItem("kara_chart_sessions", String(showSessions)); } catch {} }, [showSessions]);

    // P2 #17b CROSSHAIR BROADCAST (linkMode on)
    useEffect(() => {
      if (!linkMode) return;
      const bc = linkBCRef.current; if (!bc) return;
      const chart = chartRef.current; if (!chart) return;
      const handler = () => {
        try {
          const lr = chart.timeScale().getVisibleLogicalRange();
          if (!lr) return;
          bc.postMessage({ source: "self", from: lr.from, to: lr.to, ts: Date.now() });
        } catch {}
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(handler);
      return () => { try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch {} };
    }, [linkMode, chartInstance]);
    // P2 #18 KEYBOARD SHORTCUTS (2026-08-31)
    useEffect(() => {
      const TF_MAP = { "1":"1m","2":"5m","3":"15m","4":"1h","5":"4h","6":"1d" };
      const isTyping = () => {
        const el = document.activeElement;
        if (!el) return false;
        const tag = (el.tagName || "").toLowerCase();
        return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
      };
      const handler = (ev) => {
        if (isTyping()) return;
        if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
        const k = ev.key.toLowerCase();
        if (TF_MAP[k]) {
          try { onSetTimeframe && onSetTimeframe(TF_MAP[k]); } catch {}
          ev.preventDefault(); return;
        }
        if (k === "f") { try { chartRef.current && chartRef.current.timeScale().fitContent(); } catch {} ; ev.preventDefault(); return; }
        if (k === "+" || k === "=") { try { chartRef.current && chartRef.current.timeScale().zoomIn(); } catch {} ; ev.preventDefault(); return; }
        if (k === "-" || k === "_") { try { chartRef.current && chartRef.current.timeScale().zoomOut(); } catch {} ; ev.preventDefault(); return; }
        if (k === "l") { setLinkMode(v => !v); ev.preventDefault(); return; }
        if (k === "m") { setShowHistoryMarkers(v => !v); ev.preventDefault(); return; }
        if (k === "s") {
          try {
            const cs = chartRef.current;
            if (cs && cs.takeScreenshot) {
              const url = cs.takeScreenshot().toDataURL("image/png");
              const a = document.createElement("a");
              a.href = url; a.download = "kara-" + (symbol || "chart") + "-" + Date.now() + ".png";
              a.click();
            }
          } catch {}
          ev.preventDefault(); return;
        }
        if (k === "escape") {
          setShowPicker(false); setShowColorPicker(false); setMtfPanel(false);
          return;
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }, [onSetTimeframe, chartInstance, symbol]);
  useEffect(() => { try { localStorage.setItem("kara_chart_hour_heatmap", String(showHourHeatmap)); } catch {} }, [showHourHeatmap]);
    // P2 #16 ML SIGNAL-QUALITY BADGE (2026-08-31)
    const [mlBadge, setMlBadge] = useState({ status: "loading", win_rate_train: null, degenerate: null, last_conf: null, last_pred: null, last_sym: null });
    useEffect(() => {
      const tok = (() => { try { return localStorage.getItem("kara_auth_token") || ""; } catch { return ""; } })();
      const fetchStatus = () => {
        fetch("/api/ml/status", { headers: { Authorization: "Bearer " + tok } })
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            if (!d) { setMlBadge(b => Object.assign({}, b, { status: "unreachable" })); return; }
            setMlBadge(b => Object.assign({}, b, {
              status: d.is_trained ? (d.model_degenerate ? "degenerate" : "healthy") : "untrained",
              win_rate_train: d.win_rate_train != null ? d.win_rate_train : null,
              degenerate: d.model_degenerate === true,
            }));
          })
          .catch(() => setMlBadge(b => Object.assign({}, b, { status: "unreachable" })));
      };
      fetchStatus();
      const id = setInterval(fetchStatus, 30000);
      return () => clearInterval(id);
    }, []);
    const setMlBadgeFromPrediction = useCallback((sym, pred, conf) => {
      setMlBadge(b => Object.assign({}, b, { last_sym: sym, last_pred: pred, last_conf: conf }));
    }, []);

    // Mirror refs for imperative handle getLayout/applyLayout (CRITIC FIX #2:
  // useImperativeHandle with deps=[] captures stale closures, so we mirror
  // current state into refs and read/write through them.
  // Initialized to null/undefined to avoid TDZ — useEffects below mirror the live values.
  // These are declared EARLY (before useImperativeHandle) so the factory can read them.
  const activeIndsRef         = useRef(null);
  const customIndsRef         = useRef(null);
  // drawingsRef already exists at line 676 — reuse it for getLayout/applyLayout
  const candleColorsRef       = useRef(null);
  const showSessionsRef       = useRef(null);
  const showHourHeatmapRef    = useRef(null);
  const showHistoryMarkersRef = useRef(null);
  const showActiveMarkersRef  = useRef(null);
  const showStrategyIndRef    = useRef(null);
  // 2026-10-09 agent-ui: latest-value mirror for the agent methods on the imperative handle.
  // Assigned every render (further down, after the referenced callbacks exist).
  const agentLiveRef          = useRef({});



  // ── Expose imperative API for voice orchestrator (useChartVoice.js)
  useImperativeHandle(ref, () => ({
    addIndicator: (id) => {
      const cat = INDICATOR_CATALOGUE.find(i => i.id === id);
      if (!cat) { try { console.warn(`[agent-ui] addIndicator: unknown id "${id}"`); } catch {} return; }
      try { console.log(`[agent-ui] addIndicator ${id}`); } catch {}
      setActiveInds(prev => prev[id] ? prev : { ...prev, [id]: { ...cat } });
    },
    removeIndicator: (id) => {
      setActiveInds(prev => {
        if (!prev[id]) return prev;
        const n = { ...prev }; delete n[id]; return n;
      });
    },
    zoomIn: () => {
      try { chartRef.current?.timeScale().zoomIn(); } catch (e) { console.warn("zoomIn failed:", e); }
    },
    zoomOut: () => {
      try { chartRef.current?.timeScale().zoomOut(); } catch (e) { console.warn("zoomOut failed:", e); }
    },
    fitContent: () => {
      try { chartRef.current?.timeScale().fitContent(); } catch (e) { console.warn("fitContent failed:", e); }
    },
    fitNow: () => { try { chartRef.current?.timeScale().fitContent(); } catch (e) { console.warn("fitNow failed:", e); } },
    setLinkMode: (on) => setLinkMode(typeof on === "boolean" ? on : v => !v),
    setMlBadgeFromPrediction: (sym, pred, conf) => setMlBadgeFromPrediction(sym, pred, conf),
    toggleStrategyOverlay: (on) => setShowStrategyInd(typeof on === "boolean" ? on : s => !s),
    openColors: () => setShowColorPicker(true),
    openIndicatorPicker: () => setShowPicker(true),
    clearIndicators: () => setActiveInds({}),
    // P0.1 voice: "clear my lines" / "clear trade lines"
    clearTradeLines: () => {
      if (!seriesRef.current) return;
      Object.keys(tradeLinesRef.current).forEach(k => {
        if (k === "mlPrediction") {
          // keep ML prediction lines separate
          return;
        }
        const lines = tradeLinesRef.current[k];
        if (Array.isArray(lines)) {
          lines.forEach(line => { try { seriesRef.current.removePriceLine(line); } catch {} });
        }
        delete tradeLinesRef.current[k];
      });
    },
    // P0.2 voice: "show history" / "hide history markers"
    toggleHistoryMarkers: (on) => setShowHistoryMarkers(typeof on === "boolean" ? on : s => !s),
    // P0.2 voice: "show active" / "hide active markers"
    toggleActiveMarkers: (on) => setShowActiveMarkers(typeof on === "boolean" ? on : s => !s),
    // P0.3 voice: "draw rectangle"
    setDrawingTool: (tool) => { setDrawingMode(tool); setDrawingAnchor(null); },
    // P1 voice: "show sessions" / "hide sessions"
    toggleSessionOverlay: (on) => setShowSessions(typeof on === "boolean" ? on : s => !s),
    // P1 voice: "show hour heatmap" / "hide hour heatmap"
    toggleHourHeatmap: (on) => setShowHourHeatmap(typeof on === "boolean" ? on : s => !s),
    // ── P1 #6 LAYOUT PRESETS ──────────────────────────────────────────────
    // "save setup scalper-1" / "load scalper-1"
    // Captures the entire chart visual state into a serializable JSON object
    // that can be stored in localStorage and re-applied later. Drawings are
    // serialized as their primitives; indicator params/colors preserved.
    getLayout: () => {
      try {
        // CRITIC FIX #2: read from mirror refs (deps=[] closes over initial state).
        // symbol/timeframe are props and therefore always fresh via the closure.
        return {
          v: 1,
          ts: Date.now(),
          symbol,
          timeframe,
          activeInds:        activeIndsRef.current,
          customInds:        customIndsRef.current,
          drawings:          drawingsRef.current,
          candleColors:      candleColorsRef.current,
          overlays: {
            showSessions:       showSessionsRef.current,
            showHourHeatmap:    showHourHeatmapRef.current,
            showHistoryMarkers: showHistoryMarkersRef.current,
            showActiveMarkers:  showActiveMarkersRef.current,
            showStrategyInd:    showStrategyIndRef.current,
          },
        };
      } catch (e) {
        console.warn("getLayout failed:", e);
        return null;
      }
    },
    applyLayout: (layout) => {
      try {
        if (!layout || layout.v !== 1) {
          console.warn("applyLayout: bad layout version", layout?.v);
          return false;
        }
        // Setters always read fresh refs after re-render. State updates from setDrawings
        // etc. trigger the useEffects above which update *Ref.current.
        if (layout.activeInds) setActiveInds(layout.activeInds);
        if (layout.customInds) setCustomInds(layout.customInds);
        if (layout.drawings)   setDrawings(layout.drawings);
        if (layout.candleColors) setCandleColors(layout.candleColors);
        const ov = layout.overlays || {};
        if ("showSessions" in ov)       setShowSessions(ov.showSessions);
        if ("showHourHeatmap" in ov)    setShowHourHeatmap(ov.showHourHeatmap);
        if ("showHistoryMarkers" in ov) setShowHistoryMarkers(ov.showHistoryMarkers);
        if ("showActiveMarkers" in ov)  setShowActiveMarkers(ov.showActiveMarkers);
        if ("showStrategyInd" in ov)    setShowStrategyInd(ov.showStrategyInd);
        return true;
      } catch (e) {
        console.warn("applyLayout failed:", e);
        return false;
      }
    },
    // P1 #5 voice "how risky is R_25?" — opens risk modal with that symbol highlighted
    showRiskScore: (risk) => {
      if (risk) {
        setRiskData({ risk, lookback_days: 7, username: "current" });
      }
      setRiskModal(true);
    },
    // P1 #4 voice "show MTF" — opens a verdict panel + paints TF bands on chart
    showMtf: (mtf) => {
      if (!mtf) return;
      setMtfData(mtf);
      setMtfPanel(true);
    },
    // P1 #4 helper: voice dispatch falls back to current chart symbol if user said "show me MTF"
    getCurrentSymbol: () => symbol,
    // P2 #8 chart screenshot — already above
    // P2 #13 — voice "show equity" / "hide equity" / "show my P&L"
    toggleEquity: (on) => setShowEquityCurve(typeof on === "boolean" ? on : s => !s),
    // P2 #12 price alert voice — "alert me when R_25 hits 1.25"
    setPriceAlert: (alert) => {
      if (!alert || !alert.symbol || !alert.price) return;
      setPriceAlerts(prev => [...prev, { ...alert, id: Date.now(), createdAt: Date.now() }]);
    },
    // P2 #11 voice journal — "note: missed entry by 2 pips" / "journal: bullish bias"
    addJournalEntry: (text) => {
      if (!text) return;
      setJournalEntries(prev => {
        const next = [...prev, { id: Date.now(), ts: Date.now(), text, symbol, timeframe }];
        try { localStorage.setItem(`kara_journal_${symbol}_${timeframe}`, JSON.stringify(next.slice(-50))); } catch {}
        return next;
      });
    },
    getJournalEntries: () => journalEntriesRef.current,
    // ── P2 #8 SCREENSHOT ──────────────────────────────────────────────────
    // Native lightweight-charts takeScreenshot() → HTMLCanvasElement → PNG download
    // Zero deps. Captures the chart with crosshair; overlays are bottom-layer only.
    screenshot: () => {
      try {
        const chart = chartInstance || chartRef.current;
        if (!chart) { console.warn("screenshot: no chart"); return false; }
        const canvas = chart.takeScreenshot(true, false); // top layer ON, no crosshair
        if (!canvas) { console.warn("screenshot: takeScreenshot returned null"); return false; }
        const dataUrl = canvas.toDataURL("image/png");
        const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `kara-${symbol || "chart"}-${timeframe || "tf"}-${ts}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return true;
      } catch (e) {
        console.warn("screenshot failed:", e);
        return false;
      }
    },
    // ── 2026-10-09 agent-ui: methods driven by the Kara/Lala chat agent (ui_* tools) ──
    toggleFullscreen: (on) => {
      const L = agentLiveRef.current;
      const isFs = !!document.fullscreenElement;
      if (typeof on === "boolean" && on === isFs) return isFs;
      L.toggleFullscreen?.();
      return !isFs;
    },
    addDrawing: (spec = {}) => {
      const L = agentLiveRef.current;
      const cur = Array.isArray(L.data) ? L.data : L.data?.candles;
      const last = cur && cur.length ? cur[cur.length - 1] : null;
      const lastClose = L.lastPrice != null && isFinite(Number(L.lastPrice)) ? Number(L.lastPrice) : (last ? Number(last.close) : null);
      const lastTime = last ? Number(last.time) : Math.floor(Date.now() / 1000);
      const resolvePrice = (v) => {
        if (v === "last" || v === "current" || v == null || v === "") return lastClose;
        const n = Number(v); return isFinite(n) ? n : null;
      };
      const type = spec.type === "rect" ? "rectangle" : (spec.type || "horizontal");
      if (!["horizontal","vertical","trendline","ray","fibonacci","rectangle"].includes(type)) throw new Error("unknown drawing type " + type);
      const p1 = { time: Number(spec.p1?.time) || lastTime, price: resolvePrice(spec.p1?.price ?? spec.price) };
      if (p1.price == null) throw new Error("no price available (chart has no candles yet)");
      const d = { id: Date.now() + Math.floor(Math.random() * 1000), type, p1, color: spec.color };
      if (type !== "horizontal" && type !== "vertical") {
        if (!spec.p2) throw new Error(type + " needs p1 and p2");
        let t2 = Number(spec.p2.time) || lastTime;
        if (t2 === p1.time && cur && cur.length > 20) p1.time = Number(cur[cur.length - 20].time);
        d.p2 = { time: t2, price: resolvePrice(spec.p2.price) };
        if (d.p2.price == null) throw new Error("p2.price invalid");
      }
      const next = [...(drawingsRef.current || []), d];
      drawingsRef.current = next;
      setDrawings(next);
      try { L.renderDrawings?.(chartRef.current, seriesRef.current, next); } catch {}
      return d;
    },
    clearDrawings: () => {
      const L = agentLiveRef.current;
      drawingsRef.current = [];
      setDrawings([]); setDrawingMode(null); setDrawingAnchor(null);
      try { L.renderDrawings?.(chartRef.current, seriesRef.current, []); } catch {}
      return true;
    },
    setIndicatorParams: (id, params = {}) => {
      const cat = INDICATOR_CATALOGUE.find(i => i.id === id);
      if (!cat) return false;
      setActiveInds(prev => {
        const base = prev[id] || { ...cat };
        return { ...prev, [id]: { ...base, params: { ...(base.params || cat.params || {}), ...params } } };
      });
      return true;
    },
    getState: () => {
      const L = agentLiveRef.current;
      const cur = Array.isArray(L.data) ? L.data : L.data?.candles;
      const last = cur && cur.length ? cur[cur.length - 1] : null;
      return {
        symbol: L.symbol, timeframe: L.timeframe,
        indicators: Object.keys(L.activeInds || {}),
        drawings: (drawingsRef.current || []).map(d => ({ id: d.id, type: d.type, p1: d.p1, p2: d.p2 })).slice(-20),
        overlays: {
          sessions: !!L.showSessions, hour_heatmap: !!L.showHourHeatmap, equity: !!L.showEquityCurve,
          strategy_overlay: !!L.showStrategyInd, history_markers: !!L.showHistoryMarkers, active_markers: !!L.showActiveMarkers,
          mtf: !!L.mtfPanel, risk: !!L.riskModal, fullscreen: !!document.fullscreenElement, link: !!L.linkMode,
        },
        lastPrice: L.lastPrice != null ? L.lastPrice : (last ? last.close : null),
        candles: cur ? cur.length : 0,
      };
    },
    savePreset: (name) => {
      const L = agentLiveRef.current;
      const key = String(name || "").trim().replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9_\-]/g, "");
      if (!key) throw new Error("preset name required");
      const layout = {
        v: 1, ts: Date.now(), symbol: L.symbol, timeframe: L.timeframe,
        activeInds: L.activeInds, customInds: L.customInds, drawings: drawingsRef.current, candleColors: L.candleColors,
        overlays: { showSessions: L.showSessions, showHourHeatmap: L.showHourHeatmap, showHistoryMarkers: L.showHistoryMarkers, showActiveMarkers: L.showActiveMarkers, showStrategyInd: L.showStrategyInd },
      };
      localStorage.setItem("kara_chart_preset_" + key, JSON.stringify(layout));
      try { L.refreshPresetList?.(); } catch {}
      return key;
    },
    loadPreset: (name) => {
      const key = String(name || "").trim().replace(/\s+/g, "-").toLowerCase();
      const raw = localStorage.getItem("kara_chart_preset_" + key);
      if (!raw) return false;
      const layout = JSON.parse(raw);
      if (layout.activeInds) setActiveInds(layout.activeInds);
      if (layout.customInds) setCustomInds(layout.customInds);
      if (layout.drawings) { drawingsRef.current = layout.drawings; setDrawings(layout.drawings); }
      if (layout.candleColors) setCandleColors(layout.candleColors);
      const ov = layout.overlays || {};
      if ("showSessions" in ov) setShowSessions(ov.showSessions);
      if ("showHourHeatmap" in ov) setShowHourHeatmap(ov.showHourHeatmap);
      if ("showHistoryMarkers" in ov) setShowHistoryMarkers(ov.showHistoryMarkers);
      if ("showActiveMarkers" in ov) setShowActiveMarkers(ov.showActiveMarkers);
      if ("showStrategyInd" in ov) setShowStrategyInd(ov.showStrategyInd);
      return true;
    },
    listPresets: () => {
      try { return Object.keys(localStorage).filter(k => k.startsWith("kara_chart_preset_")).map(k => k.replace("kara_chart_preset_", "")); }
      catch { return []; }
    },
    openMtf: async (on = true) => {
      const L = agentLiveRef.current;
      try { localStorage.setItem("kara_mtf_panel_visible", String(!!on)); } catch {}
      if (!on) { setMtfPanel(false); return null; }
      setMtfPanel(true);
      const sym = L.symbol;
      setMtfData({ symbol: sym, alignment: "PENDING", voice: "Analyzing…", tfs: [] });
      try {
        const r = await fetch(`/api/analytics/multi-tf-analysis?symbol=${encodeURIComponent(sym)}`, {
          headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
        });
        const d = r.ok ? await r.json() : null;
        if (d) setMtfData(d);
        return d ? { symbol: d.symbol, alignment: d.alignment, confidence: d.confidence, voice: d.voice } : null;
      } catch { return null; }
    },
    openRisk: async (on = true) => {
      if (!on) { setRiskModal(false); return null; }
      setRiskModal(true);
      try {
        let username = "ken72";
        try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); if (u?.username) username = u.username; } catch {}
        const q = new URLSearchParams({ username, days: "7" });
        const r = await fetch(`/api/analytics/risk-scores?${q}`, {
          headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
        });
        const d = r.ok ? await r.json() : null;
        if (d) setRiskData(d);
        return d;
      } catch { return null; }
    },
  }), [chartInstance]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  // P3 mobile-toolbar 2026-08-31: detect viewport width so toolbar collapses into a "⋮ More" drawer on phones
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  useEffect(() => {
    const onR = () => { setIsMobile(window.innerWidth <= 768); if (window.innerWidth > 768) setShowMoreMenu(false); };
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const [candleColors, setCandleColors] = useState(() => { const saved = localStorage.getItem("chartCandleColors"); return saved ? JSON.parse(saved) : { up: "#00d4aa", down: "#ef4444", wick: "#94a3b8" }; });
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Active indicators: map of id → {enabled, params, color}
  const [activeInds, setActiveInds] = useState(() => {
    try { const v = localStorage.getItem("kara_chart_inds"); if (v) return JSON.parse(v); } catch {}
    return { ema20: { ...INDICATOR_CATALOGUE.find(i => i.id==="ema20") } };
  });

    // P2 #19 VOLUME PROFILE (2026-08-31)
    useEffect(() => {
      if (!activeInds["volumeprofile"]) return;
      const cd = Array.isArray(dataRef.current) ? dataRef.current : dataRef.current?.candles;
      if (!cd || cd.length < 30) return;
      const params = (activeInds["volumeprofile"] && activeInds["volumeprofile"].params) || {};
      const profile = calcVolumeProfile(cd, params.bins || 24);
      if (!profile || profile.poc == null || !isFinite(profile.poc)) return;
      const yc = seriesRef.current; if (!yc) return;
      const tag = "vp_lines";
      const prev = tradeLinesRef.current[tag] || [];
      prev.forEach(l => { try { yc.removePriceLine(l); } catch {} });
      try {
        const pocLine = yc.createPriceLine({ price: profile.poc, color: "#fbbf24", lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: "POC " + Number(profile.poc).toFixed(2) });
        const vahLine = yc.createPriceLine({ price: profile.vah, color: "#fbbf24", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "VAH " + Number(profile.vah).toFixed(2) });
        const valLine = yc.createPriceLine({ price: profile.val, color: "#fbbf24", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "VAL " + Number(profile.val).toFixed(2) });
        tradeLinesRef.current[tag] = [pocLine, vahLine, valLine];
        return () => {
          try { [pocLine, vahLine, valLine].forEach(l => yc.removePriceLine(l)); } catch {}
          delete tradeLinesRef.current[tag];
        };
      } catch (err) {
        console.warn("[ChartWidget] volumeProfile line error:", err);
      }
    }, [activeInds && activeInds["volumeprofile"], data]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerCat,  setPickerCat]  = useState("Moving Averages");
  const [editInd,    setEditInd]    = useState(null); // id being param-edited

  // Custom indicators: array of {id, formula, color, label, pane}
  const [customInds, setCustomInds] = useState(() => {
    try { const v = localStorage.getItem("kara_chart_custom"); if (v) return JSON.parse(v); } catch {}
    return [];
  });
  const [customFormula, setCustomFormula] = useState("");
  const [customLabel,   setCustomLabel]   = useState("");
  const [customColor,   setCustomColor]   = useState("#f59e0b");
  const [customPane,    setCustomPane]    = useState("main");
  const [customError,   setCustomError]   = useState("");

  const [mlPredictionOverlay, setMlPredictionOverlay] = useState(null);
  const [showStrategyInd, setShowStrategyInd] = useState(() => {
    try { return localStorage.getItem("kara_chart_strat") === "true"; } catch { return false; }
  });

  useEffect(() => { try { localStorage.setItem("kara_chart_inds", JSON.stringify(activeInds)); } catch {} }, [activeInds]);
  useEffect(() => { try { localStorage.setItem("kara_chart_custom", JSON.stringify(customInds)); } catch {} }, [customInds]);
  useEffect(() => { try { localStorage.setItem("kara_chart_strat", String(showStrategyInd)); } catch {} }, [showStrategyInd]);

  // Update candlestick colors when they change
  useEffect(() => {
    if (seriesRef.current) {
      try {
        seriesRef.current.applyOptions({
          upColor: candleColors.up,
          downColor: candleColors.down,
          wickUpColor: candleColors.up,
          wickDownColor: candleColors.down
        });
      } catch (e) {
        console.error('Failed to update candlestick colors:', e);
      }
    }
    try { localStorage.setItem("chartCandleColors", JSON.stringify(candleColors)); } catch {}
  }, [candleColors]);

  // Trade modal state
  const [tradeModal, setTradeModal] = useState(null); // {direction:"CALL"|"PUT"} or null
  const [, forceRender] = useState(0); // used for countdown tick
  const [tradeForm, setTradeForm] = useState({
    entry: null, tp1: null, tp2: null, sl: null, stake: stake, duration: "5", duration_unit: "m"
  });

  // Professional chart features
  const [volumeProfile, setVolumeProfile] = useState(null);
  const [priceAlerts, setPriceAlerts]     = useState([]);
  const [firedAlerts, setFiredAlerts]     = useState([]);   // alerts that have triggered (max 20)
  // P2 #13 equity curve sub-pane (BaselineSeries)
  const [showEquityCurve, setShowEquityCurve] = useState(() => { try { return localStorage.getItem("kara_chart_equity") === "true"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem("kara_chart_equity", String(showEquityCurve)); } catch {} }, [showEquityCurve]);
  const [equityCurve, setEquityCurve]         = useState(null);
  const [equitySummary, setEquitySummary]     = useState(null);
  const equitySeriesRef = useRef(null);
  const [drawingMode, setDrawingMode]     = useState(null); // 'trendline'|'horizontal'|'fibonacci'|null
  const [drawings, setDrawings]           = useState([]);   // persisted drawings
  const [drawingAnchor, setDrawingAnchor] = useState(null); // first click {time, price}
  useEffect(() => { activeIndsRef.current         = activeInds; }, [activeInds]);
  useEffect(() => { customIndsRef.current         = customInds; }, [customInds]);
  useEffect(() => { candleColorsRef.current       = candleColors; }, [candleColors]);
  useEffect(() => { showSessionsRef.current       = showSessions; }, [showSessions]);
  useEffect(() => { showHourHeatmapRef.current    = showHourHeatmap; }, [showHourHeatmap]);
  useEffect(() => { showHistoryMarkersRef.current = showHistoryMarkers; }, [showHistoryMarkers]);
  useEffect(() => { showActiveMarkersRef.current  = showActiveMarkers; }, [showActiveMarkers]);
  useEffect(() => { showStrategyIndRef.current    = showStrategyInd; }, [showStrategyInd]);
  // refs for drawing — avoids stale closure inside subscribeClick
  const drawingModeRef   = useRef(null);
  const drawingAnchorRef = useRef(null);
  const drawingsRef      = useRef([]);       // mirror of drawings state for render fn
  const drawingSeriesRef = useRef([]);       // LineSeries refs used for drawings (so we can destroy them)
  useEffect(() => { drawingModeRef.current   = drawingMode;   }, [drawingMode]);
  useEffect(() => { drawingAnchorRef.current = drawingAnchor; }, [drawingAnchor]);
  useEffect(() => { drawingsRef.current      = drawings;      }, [drawings]);

  const [drawingModal, setDrawingModal]   = useState(false);
  const [alertModal, setAlertModal]       = useState(null);
  // P1 #6 layout presets modal
  const [presetModal, setPresetModal]     = useState(false);   // 'save'|'load'|null
  const [presetName, setPresetName]       = useState("");       // typed name for save
  const [presetList, setPresetList]       = useState([]);       // names of saved presets
  // P1 #5 per-symbol risk score panel
  const [riskModal, setRiskModal]         = useState(false);
  const [riskData, setRiskData]           = useState(null);     // {risks: [...]} or {risk: {...}}
  const [riskLoading, setRiskLoading]     = useState(false);
  const [lastRiskCall, setLastRiskCall]   = useState(0);        // throttle to 1 fetch / 5s
  const fetchRiskScores = async (symFilter = null) => {
    const now = Date.now();
    if (now - lastRiskCall < 5000) return;
    setLastRiskCall(now);
    setRiskLoading(true);
    try {
      let username = "ken72";
      try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); if (u?.username) username = u.username; } catch {}
      const q = new URLSearchParams({ username, days: "7" });
      if (symFilter) q.set("symbol", symFilter);
      const r = await fetch(`/api/analytics/risk-scores?${q}`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
      });
      if (!r.ok) { console.warn("risk-scores fetch failed", r.status); return; }
      const d = await r.json();
      setRiskData(d);
      if (symFilter && d.risk) setRiskModal(true);
    } catch (e) {
      console.warn("risk-scores fetch error:", e);
    } finally {
      setRiskLoading(false);
    }
  };

  // P1 #4 multi-timeframe verdict panel
  // P1 #4 multi-timeframe verdict panel — DEFAULT ON per Kenyon 2026-09-13 voice note.
  //   Persistent across reloads via localStorage so user-toggle-off is remembered.
  //   see the panel JSX at the bottom of the chart container.
  const [mtfPanel, setMtfPanel]   = useState(() => {
    try {
      const stored = localStorage.getItem('kara_mtf_panel_visible');
      if (stored === 'false') return false;
      if (stored === 'true')  return true;
    } catch {}
    return true; // default ON if no preference
  });
  const [mtfData, setMtfData]     = useState(null);   // {symbol, alignment, confidence, tfs[], voice}

  const handleToggleMtf = useCallback((dataOverride = null) => {
    if (dataOverride) {
      setMtfData(dataOverride);
      setMtfPanel(true);
      try { localStorage.setItem('kara_mtf_panel_visible', 'true'); } catch {}
      return;
    }
    setMtfPanel(prev => {
      const next = !prev;
      try { localStorage.setItem('kara_mtf_panel_visible', String(next)); } catch {}
      if (next) {
        setMtfData({ symbol, alignment: "PENDING", voice: "Analyzing…", tfs: [] });
        fetch(`/api/analytics/multi-tf-analysis?symbol=${encodeURIComponent(symbol)}`, {
          headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` }
        })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setMtfData(d); })
          .catch(() => {});
      }
      return next;
    });
  }, [symbol]);

  // P1 #4 MTF auto-fetch: when mtfPanel is visible and the symbol changes (or first mount),
  //   refetch verdict without requiring user to click the toolbar button again.
  useEffect(() => {
    if (!mtfPanel || !symbol) return;
    let cancelled = false;
    setMtfData(prev => prev && prev.symbol === symbol ? prev : { symbol, alignment: "PENDING", voice: "Analyzing…", tfs: [] });
    fetch(`/api/analytics/multi-tf-analysis?symbol=${encodeURIComponent(symbol)}`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setMtfData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol, mtfPanel]);

  const handleScreenshot = useCallback(() => {
    try {
      const chart = chartInstance || chartRef.current;
      if (!chart) { console.warn("screenshot: no chart"); return false; }
      const canvas = chart.takeScreenshot(true, false); // top layer ON, no crosshair
      if (!canvas) { console.warn("screenshot: takeScreenshot returned null"); return false; }
      const dataUrl = canvas.toDataURL("image/png");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `kara-${symbol || "chart"}-${timeframe || "tf"}-${ts}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (e) {
      console.warn("screenshot failed:", e);
      return false;
    }
  }, [chartInstance, symbol, timeframe]);

  // P2 #11 voice journal entries (per symbol+timeframe, persisted to localStorage)
  const [journalEntries, setJournalEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`kara_journal_default`) || "[]"); } catch { return []; }
  });

  const journalEntriesRef = useRef(journalEntries);
  useEffect(() => { journalEntriesRef.current = journalEntries; }, [journalEntries]);

  // P2 #13 — paint equity curve into sub-chart when toggle + data are ready
  useEffect(() => {
    const subChart = subChartRef.current;
    if (!subChart) return;
    if (equitySeriesRef.current) {
      try { subChart.removeSeries(equitySeriesRef.current); } catch {}
      equitySeriesRef.current = null;
    }
    if (!showEquityCurve || !equityCurve || equityCurve.length < 2) return;
    try {
      const series = subChart.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: 0 },
        topLineColor: "#10b981", topFillColor1: "rgba(16,185,129,0.25)", topFillColor2: "rgba(16,185,129,0.02)",
        bottomLineColor: "#ef4444", bottomFillColor1: "rgba(239,68,68,0.02)", bottomFillColor2: "rgba(239,68,68,0.25)",
        priceLineVisible: false, lastValueVisible: true, title: "Equity $",
      });
      series.setData(equityCurve.map(p => ({ time: p.time, value: p.cum })));
      equitySeriesRef.current = series;
    } catch (e) { console.warn("equity render failed:", e); }
    return () => {
      if (equitySeriesRef.current) {
        try { subChart.removeSeries(equitySeriesRef.current); } catch {}
        equitySeriesRef.current = null;
      }
    };
  }, [showEquityCurve, equityCurve]);

  // P2 #13 EQUITY CURVE fetcher — runs when showEquityCurve toggles or symbol changes
  const fetchEquityCurve = useCallback(async () => {
    try {
      let username = "ken72";
      try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); if (u?.username) username = u.username; } catch {}
      const r = await fetch("/api/equity-curve?username=" + encodeURIComponent(username) + "&days=30&symbol=" + encodeURIComponent(symbol), {
        headers: { "Authorization": "Bearer " + (localStorage.getItem("kara_auth_token") || "") },
      });
      if (!r.ok) { console.warn("equity-curve fetch failed", r.status); return; }
      const d = await r.json();
      setEquityCurve(d.curve || []);
      setEquitySummary(d.summary || null);
    } catch (e) { console.warn("equity-curve fetch error:", e); }
  }, [symbol]);
  useEffect(() => { if (showEquityCurve) fetchEquityCurve(); }, [showEquityCurve, fetchEquityCurve]);

  // P2 #12 PRICE ALERT POLLING — fires when live price crosses alert threshold
  // Works for current chart symbol only (multi-chart would need cross-chart broadcast)
  useEffect(() => {
    if (priceAlerts.length === 0 || lastPrice == null) return;
    const nowPrice = lastPrice;
    const stillActive = [];
    for (const alert of priceAlerts) {
      if (alert.symbol !== symbol) { stillActive.push(alert); continue; }  // only fire on this chart's symbol
      const fired = (alert.condition === "above" && nowPrice >= alert.price) ||
                    (alert.condition === "below" && nowPrice <= alert.price);
      if (fired) {
        // Speak + add to fired list + remove from active
        try { onSpeak?.(`Alert: ${symbol} ${alert.condition} ${alert.price.toFixed(5)} hit at ${nowPrice.toFixed(5)}`); } catch {}
        setFiredAlerts(prev => [...prev, { ...alert, ts: Date.now(), hitPrice: nowPrice }]);
      } else {
        stillActive.push(alert);
      }
    }
    if (stillActive.length !== priceAlerts.length) setPriceAlerts(stillActive);
  }, [lastPrice, symbol]);  // eslint-disable-line react-hooks/exhaustive-deps
  const refreshPresetList = () => {
    try {
      const list = Object.keys(localStorage)
        .filter(k => k.startsWith("kara_chart_preset_"))
        .map(k => k.replace("kara_chart_preset_", ""));
      setPresetList(list);
    } catch { setPresetList([]); }
  };

  const getSorted = useCallback(() => {
    const raw = Array.isArray(data) ? data : data?.candles;
    if (!raw || !raw.length) return [];
    const s = [...raw].sort((a,b) => a.time - b.time);
    const u = []; let last = 0;
    for (const c of s) { if (c.time > last) { u.push(c); last = c.time; } else if (c.time === last) u[u.length-1] = c; }
    return u;
  }, [data]);

  // ── Create main chart ─────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: { background: { color:"#0d1f35" }, textColor:"#e2e8f0" },
      grid:   { vertLines:{color:"#1e3a5f"}, horzLines:{color:"#1e3a5f"} },
      crosshair: { mode:1 },
      timeScale: { timeVisible:true, secondsVisible:false, borderColor:"#1e3a5f", rightOffset:5, barSpacing:8, minBarSpacing:2 },
      rightPriceScale: { borderColor:"#1e3a5f", scaleMargins:{top:0.1,bottom:0.1} },
      handleScroll: { mouseWheel:true, pressedMouseMove:true, horzTouchDrag:true },
      handleScale:  { axisPressedMouseMove:true, mouseWheel:true, pinch:true, axisDoubleClickReset:true },
      width: containerRef.current.clientWidth, height,
    });
    const series = chart.addSeries(CandlestickSeries, { upColor:candleColors.up, downColor:candleColors.down, borderVisible:false, wickUpColor:candleColors.up, wickDownColor:candleColors.down });
    chartRef.current = chart; seriesRef.current = series; setChartInstance(chart);
    const onResize = () => containerRef.current && chart.applyOptions({ width: containerRef.current.clientWidth });
    window.addEventListener("resize", onResize);

    // P2 #14 — smart crosshair: publish OHLCV legend from crosshair position
    chart.subscribeCrosshairMove(param => {
      try {
        if (!param || !param.time) { setCrosshairOHLC(null); return; }
        // Use dataRef to avoid stale closure on prop changes (chart redraws on every data update)
        const cur = dataRef && dataRef.current ? (Array.isArray(dataRef.current) ? dataRef.current : dataRef.current.candles) : null;
        if (!cur || !cur.length) { setCrosshairOHLC(null); return; }
        const candle = cur.find(c => c && (c.time === param.time || c.time === param.time * 1000));
        if (!candle) { setCrosshairOHLC(null); return; }
        setCrosshairOHLC({ time: param.time, open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume });
      } catch (e) { setCrosshairOHLC(null); }
    });

    // ── Drawing Tool Click Handler ─────────────────────────────────────────
    // param.point.y gives the Y pixel — convert to price via coordinateToPrice
    chart.subscribeClick((param) => {
      const mode = drawingModeRef.current;
      if (!mode || !param.point) return;

      // Get price at the clicked Y coordinate
      const price = series.coordinateToPrice(param.point.y);
      if (price === null || !isFinite(price)) return;

      // Time from clicked point (or fallback to latest candle time)
      const cur = dataRef && dataRef.current ? (Array.isArray(dataRef.current) ? dataRef.current : dataRef.current.candles) : null;
      const lastCandle = cur && cur.length ? cur[cur.length - 1] : null;
      const time = param.time || (lastCandle ? lastCandle.time : Math.floor(Date.now() / 1000));

      const anchor = drawingAnchorRef.current;

      if (mode === 'horizontal' || mode === 'vertical') {
        // Single click drawings: draw immediately
        const d = { id: Date.now(), type: mode, p1: { time, price } };
        setDrawings(prev => { const next = [...prev, d]; drawingsRef.current = next; return next; });
        setDrawingAnchor(null);
        renderDrawings(chart, series, [...drawingsRef.current, d]);
        return;
      }

      if (!anchor) {
        // First click — set anchor
        setDrawingAnchor({ time, price });
      } else {
        // Second click — complete drawing
        const d = { id: Date.now(), type: mode, p1: anchor, p2: { time, price } };
        setDrawings(prev => { const next = [...prev, d]; drawingsRef.current = next; return next; });
        setDrawingAnchor(null);
        renderDrawings(chart, series, drawingsRef.current);
      }
    });

    return () => {
      window.removeEventListener("resize", onResize);
      if (markersPrimitiveRef.current?.detach) {
        try { markersPrimitiveRef.current.detach(); } catch {}
      }
      markersPrimitiveRef.current = null;
      chart.remove();
    };
  }, [height]); // eslint-disable-line

  // ── renderDrawings: wipes old drawing series + price lines, re-draws all ──
  const drawingPriceLinesRef = useRef([]);  // Track price lines created by renderDrawings so they can be removed
  const sessionMarkersRef    = useRef([]);  // Track session divider handles ({type, handle}) so they can be cleaned up
  const renderDrawings = useCallback((chart, series, drawingList) => {
    if (!chart || !series) return;
    // destroy previous drawing series
    drawingSeriesRef.current.forEach(s => { try { chart.removeSeries(s); } catch {} });
    drawingSeriesRef.current = [];
    // destroy previous drawing price lines (horizontal/fib/rectangle top+bottom)
    drawingPriceLinesRef.current.forEach(l => { try { series.removePriceLine(l); } catch {} });
    drawingPriceLinesRef.current = [];

    drawingList.forEach(d => {
      try {
        if (d.type === 'horizontal') {
          // Dashed amber price level — single horizontal line
          const line = series.createPriceLine({ price: d.p1.price, color: '#f59e0b', lineWidth: 2, lineStyle: 1, axisLabelVisible: true, title: `H ${d.p1.price.toFixed(5)}` });
          drawingPriceLinesRef.current.push(line);

        } else if (d.type === 'trendline') {
          // True diagonal trend line — 2-point LineSeries
          const s = chart.addSeries(LineSeries, { color:'#3b82f6', lineWidth:2, priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false });
          const t1 = d.p1.time;
          const t2 = d.p2.time === t1 ? t1 + 1 : d.p2.time;
          s.setData([{ time: t1, value: d.p1.price }, { time: t2, value: d.p2.price }].sort((a,b)=>a.time-b.time));
          drawingSeriesRef.current.push(s);

        } else if (d.type === 'fibonacci') {
          const lo  = Math.min(d.p1.price, d.p2.price);
          const hi  = Math.max(d.p1.price, d.p2.price);
          const rng = hi - lo;
          const fibs = [[0,'0%','#a855f7'],[0.236,'23.6%','#a855f788'],[0.382,'38.2%','#22d3ee'],[0.5,'50%','#f59e0b'],[0.618,'61.8%','#22d3ee'],[0.786,'78.6%','#a855f788'],[1,'100%','#a855f7']];
          fibs.forEach(([lvl, label, color]) => {
            try {
              const line = series.createPriceLine({ price: lo + rng * lvl, color, lineWidth: lvl===0||lvl===1?2:1, lineStyle:1, axisLabelVisible:true, title: label });
              drawingPriceLinesRef.current.push(line);
            } catch {}
          });

        } else if (d.type === 'rectangle') {
          // P0.3 Rectangle drawing — top/bottom horizontal price lines + 2 vertical edge LineSeries.
          // Uses 2 diagonal corners; derives top/bottom from min/max price, left/right from min/max time.
          const lo  = Math.min(d.p1.price, d.p2.price);
          const hi  = Math.max(d.p1.price, d.p2.price);
          const tLo = Math.min(d.p1.time,  d.p2.time);
          const tHi = Math.max(d.p1.time,  d.p2.time);
          const color = d.color || '#22d3ee';
          // Top + bottom horizontal price lines — tracked so Clear All removes them
          try {
            const topLine = series.createPriceLine({ price: hi, color, lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: `▔ ${hi.toFixed(5)}` });
            drawingPriceLinesRef.current.push(topLine);
          } catch {}
          try {
            const botLine = series.createPriceLine({ price: lo, color, lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: `▁ ${lo.toFixed(5)}` });
            drawingPriceLinesRef.current.push(botLine);
          } catch {}
          // Vertical edges: LineSeries with 2 points at DIFFERENT times but SAME price range
          // lightweight-charts requires unique time values per series, so we use tLo and tHi as anchors.
          const tDiag2 = tHi === tLo ? tLo + 1 : tHi;
          const diag = chart.addSeries(LineSeries, { color, lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
          diag.setData([{ time: tLo, value: lo }, { time: tDiag2, value: hi }]);
          drawingSeriesRef.current.push(diag);
        } else if (d.type === 'ray') {
          // Ray / Extended Trend Line: project from p1 through p2 into future
          const s = chart.addSeries(LineSeries, { color: d.color || '#38bdf8', lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
          const t1 = d.p1.time;
          const t2 = d.p2.time === t1 ? t1 + 60 : d.p2.time;
          const dt = t2 - t1;
          const dp = d.p2.price - d.p1.price;
          const slope = dt !== 0 ? dp / dt : 0;
          // Project 50 periods forward
          const t3 = t2 + Math.max(3600, Math.abs(dt) * 10);
          const p3 = d.p2.price + slope * (t3 - t2);
          s.setData([
            { time: Math.min(t1, t2), value: t1 < t2 ? d.p1.price : d.p2.price },
            { time: Math.max(t1, t2), value: t1 < t2 ? d.p2.price : d.p1.price },
            { time: t3, value: p3 }
          ]);
          drawingSeriesRef.current.push(s);
        } else if (d.type === 'vertical') {
          // Vertical time line across price scale
          const s = chart.addSeries(LineSeries, { color: d.color || '#eab308', lineWidth: 2, lineStyle: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
          const curData = getSorted();
          if (curData && curData.length > 0) {
            const minP = Math.min(...curData.map(c => c.low));
            const maxP = Math.max(...curData.map(c => c.high));
            const t = d.p1.time;
            s.setData([
              { time: t, value: minP * 0.999 },
              { time: t + 1, value: maxP * 1.001 }
            ]);
            drawingSeriesRef.current.push(s);
          }
        }
      } catch {}
    });
  }, [getSorted]);


  // ── Load candle data ──────────────────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current) return;
    const c = getSorted(); if (!c.length) return;
    const isHA = !!activeInds["heikin"];
    try { 
      if (isHA) {
        seriesRef.current.setData(calcHeikinAshi(c));
      } else {
        seriesRef.current.setData(c);
      }
    } catch(e) { console.error(e); }
  }, [data?.candles, getSorted, activeInds]);

  // ── Draw indicators whenever active set or candles change ─────────
  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    const candles = getSorted(); if (!candles.length) return;

    // Tear down old indicator series
    Object.values(indicatorRefs.current).flat().forEach(s => { try { chart.removeSeries(s); } catch {} });
    indicatorRefs.current = {};
    // Tear down old custom series
    customSeriesRefs.current.forEach(s => { try { chart.removeSeries(s); } catch {} });
    customSeriesRefs.current = [];
    if (subChartRef.current) { try { subChartRef.current.remove(); } catch {} subChartRef.current = null; }

    const hasSubPane = Object.keys(activeInds).some(id => {
      const def = INDICATOR_CATALOGUE.find(i => i.id === id);
      return def?.pane === "sub";
    });

    const addLine  = (d, color, w=1, style=0) => { const s = chart.addSeries(LineSeries, { color, lineWidth:w, lineStyle:style, priceLineVisible:false, lastValueVisible:false }); s.setData(d); return s; };

    Object.entries(activeInds).forEach(([id, conf]) => {
      const def = INDICATOR_CATALOGUE.find(i => i.id === id); if (!def) return;
      const p = { ...def.params, ...conf.params };
      const color = conf.color || def.color;
      try {

      if (def.pane === "main") {
        if (def.type==="ema")        { indicatorRefs.current[id] = [addLine(calcEMA(candles, p.period), color)]; }
        else if (def.type==="sma")   { indicatorRefs.current[id] = [addLine(calcSMA(candles, p.period), color)]; }
        else if (def.type==="wma")   { indicatorRefs.current[id] = [addLine(calcWMA(candles, p.period), color)]; }
        else if (def.type==="hma")   { indicatorRefs.current[id] = [addLine(calcHMA(candles, p.period), color)]; }
        else if (def.type==="dema")  { indicatorRefs.current[id] = [addLine(calcDEMA(candles, p.period), color)]; }
        else if (def.type==="tema")  { indicatorRefs.current[id] = [addLine(calcTEMA(candles, p.period), color)]; }
        else if (def.type==="tma")   { indicatorRefs.current[id] = [addLine(calcTMA(candles, p.period), color)]; }
        else if (def.type==="vwap")  { indicatorRefs.current[id] = [addLine(calcVWAP(candles), color, 2)]; }
        else if (def.type==="bb")    { const {mid,upper,lower}=calcBB(candles,p.period,p.mult); indicatorRefs.current[id]=[addLine(mid,color),addLine(upper,color,1,2),addLine(lower,color,1,2)]; }
        else if (def.type==="kc")    { const rows=calcKC(candles,p.period,p.mult); const mk=(k)=>rows.map(r=>r[k]); indicatorRefs.current[id]=[addLine(mk("mid"),color),addLine(mk("upper"),color,1,2),addLine(mk("lower"),color,1,2)]; }
        else if (def.type==="donchian") { const {upper,lower,mid}=calcDonchian(candles,p.period); indicatorRefs.current[id]=[addLine(upper,color),addLine(lower,color),addLine(mid,color,1,2)]; }
        else if (def.type==="tmacg") { const {mid,upper,lower}=calcTMACG(candles,p.halfLength,p.bandsDeviations); indicatorRefs.current[id]=[addLine(mid,color),addLine(upper,color,1,2),addLine(lower,color,1,2)]; }
        else if (def.type==="tmabands") { const {mid,upper,lower}=calcTMABands(candles,p.period,p.mult); indicatorRefs.current[id]=[addLine(mid,color),addLine(upper,color,1,2),addLine(lower,color,1,2)]; }
        else if (def.type==="supertrend") {
          // Recompute with direction so we can split into up/down series for color
          const atr = calcATR(candles, p.period);
          const atrMap = Object.fromEntries(atr.map(x => [x.time, x.value]));
          let dir = 1, pU = 0, pL = 0, pC = candles[0]?.close ?? 0;
          const upPts = [], dnPts = [];
          for (const c of candles) {
            const a = atrMap[c.time]; if (a == null) { pC = c.close; continue; }
            const hl2 = (c.high + c.low) / 2;
            let upper = hl2 + p.mult * a, lower = hl2 - p.mult * a;
            if (pL) lower = (lower > pL || pC < pL) ? lower : pL;
            if (pU) upper = (upper < pU || pC > pU) ? upper : pU;
            if (dir === 1 && c.close < lower) dir = -1;
            else if (dir === -1 && c.close > upper) dir = 1;
            (dir === 1 ? upPts : dnPts).push({ time: c.time, value: dir === 1 ? lower : upper });
            pU = upper; pL = lower; pC = c.close;
          }
          const upS = chart.addSeries(LineSeries, { color: "#00d4aa", lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
          const dnS = chart.addSeries(LineSeries, { color: "#ef4444", lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
          upS.setData(upPts); dnS.setData(dnPts);
          indicatorRefs.current[id] = [upS, dnS];
        }
        else if (def.type==="psar") {
          // Recompute direction so we can split into bull/bear series for color
          let bull = true, sar = candles[0].low, ep = candles[0].high, af = p.step;
          const bullPts = [], bearPts = [];
          for (let i = 1; i < candles.length; i++) {
            const c = candles[i], prev = candles[i - 1];
            const prev2 = i >= 2 ? candles[i - 2] : prev;
            sar = sar + af * (ep - sar);
            if (bull) {
              if (c.low < sar) { bull = false; sar = ep; ep = c.low; af = p.step; }
              else { if (c.high > ep) { ep = c.high; af = Math.min(af + p.step, p.max); } sar = Math.min(sar, prev.low, prev2.low); }
            } else {
              if (c.high > sar) { bull = true; sar = ep; ep = c.high; af = p.step; }
              else { if (c.low < ep) { ep = c.low; af = Math.min(af + p.step, p.max); } sar = Math.max(sar, prev.high, prev2.high); }
            }
            if (!isFinite(sar)) continue;
            (bull ? bullPts : bearPts).push({ time: c.time, value: sar });
          }
          // Parabolic SAR is shown as DOTS — use HistogramSeries with zero lineWidth won't render dots.
          // Use LineSeries with pointMarkersVisible + markers via createSeriesMarkers
          const bullS = chart.addSeries(LineSeries, { color: "#00d4aa", lineWidth: 0, priceLineVisible: false, lastValueVisible: false, pointMarkersVisible: true });
          const bearS = chart.addSeries(LineSeries, { color: "#ef4444", lineWidth: 0, priceLineVisible: false, lastValueVisible: false, pointMarkersVisible: true });
          bullS.setData(bullPts); bearS.setData(bearPts);
          indicatorRefs.current[id] = [bullS, bearS];
        }
        else if (def.type==="ichimoku") {
          const {tenkan,kijun,senkouA,senkouB,chikou}=calcIchimoku(candles);
          indicatorRefs.current[id]=[addLine(tenkan,"#3b82f6"),addLine(kijun,"#ef4444"),addLine(senkouA,"#00d4aa44"),addLine(senkouB,"#f8717144"),addLine(chikou,"#a78bfa",1,1)];
        }
        else if (def.type==="vwma") {
          indicatorRefs.current[id] = [addLine(calcVWMA(candles, p.period), color, 2)];
        }
        else if (def.type==="zigzag") {
          indicatorRefs.current[id] = [addLine(calcZigZag(candles, p.depth, p.deviation, p.backstep), color, 2)];
        }
        else if (def.type==="pivot_classic") {
          const {pp, r1, r2, r3, s1, s2, s3} = calcPivotsClassic(candles);
          indicatorRefs.current[id] = [
            addLine(pp, "#eab308", 2),
            addLine(r1, "#ef4444", 1, 2), addLine(r2, "#dc2626", 1, 2), addLine(r3, "#b91c1c", 1, 2),
            addLine(s1, "#22c55e", 1, 2), addLine(s2, "#16a34a", 1, 2), addLine(s3, "#15803d", 1, 2),
          ];
        }
        else if (def.type==="pivot_camarilla") {
          const {h4, h3, l3, l4} = calcPivotsCamarilla(candles);
          indicatorRefs.current[id] = [
            addLine(h4, "#ec4899", 2), addLine(h3, "#f43f5e", 1, 2),
            addLine(l3, "#06b6d4", 1, 2), addLine(l4, "#3b82f6", 2),
          ];
        }
        else if (def.type==="chandelier") {
          const { longStop, shortStop } = calcChandelierExit(candles, p.period, p.mult);
          indicatorRefs.current[id] = [
            addLine(longStop, "#22c55e", 2),
            addLine(shortStop, "#ef4444", 2)
          ];
        }
      }
        } catch(e) { console.warn(`Indicator ${id} (${def.type}) failed:`, e); }
    });

    // Sub-pane
    if (hasSubPane && wrapperRef.current) {
      const subEl = wrapperRef.current.querySelector(".sub-pane"); if (!subEl) return;
      const subChart = createChart(subEl, {
        layout: { background:{color:"#070d1a"}, textColor:"#64748b" },
        grid:   { vertLines:{color:"#1e293b"}, horzLines:{color:"#1e293b"} },
        timeScale: { timeVisible:false, borderColor:"#1e293b" },
        rightPriceScale: { borderColor:"#1e293b", scaleMargins:{top:0.1,bottom:0.1} },
        handleScroll:{mouseWheel:true}, handleScale:{mouseWheel:true,pinch:true},
        crosshair:{mode:1}, width:subEl.clientWidth, height:110,
      });
      subChartRef.current = subChart;
      const addSubLine = (d,color,w=1) => { const s=subChart.addSeries(LineSeries,{color,lineWidth:w,priceLineVisible:false,lastValueVisible:true}); s.setData(d); return s; };
      // Sub-pane resize listener (main chart already has one)
      const onSubResize = () => { if (subEl && subChart) subChart.applyOptions({ width: subEl.clientWidth }); };
      window.addEventListener('resize', onSubResize);

      Object.entries(activeInds).forEach(([id, conf]) => {
        const def = INDICATOR_CATALOGUE.find(i => i.id === id); if (!def || def.pane !== "sub") return;
        const p = { ...def.params, ...conf.params }; const color = conf.color || def.color;
        try {
        if (def.type==="rsi")      { addSubLine(calcRSI(candles,p.period),color); }
        else if (def.type==="cci") { addSubLine(calcCCI(candles,p.period),color); }
        else if (def.type==="mfi") { addSubLine(calcMFI(candles,p.period),color); }
        else if (def.type==="willr") { addSubLine(calcWilliamsR(candles,p.period),color); }
        else if (def.type==="roc") { addSubLine(calcROC(candles,p.period),color); }
        else if (def.type==="momentum") { addSubLine(calcMomentum(candles,p.period),color); }
        else if (def.type==="adx") { addSubLine(calcADX(candles,p.period),color,2); }
        else if (def.type==="aroon") { const {up,dn}=calcAroon(candles,p.period); addSubLine(up,"#00d4aa"); addSubLine(dn,"#ef4444"); }
        else if (def.type==="atr") { addSubLine(calcATR(candles,p.period),color); }
        else if (def.type==="obv") { addSubLine(calcOBV(candles),color); }
        else if (def.type==="cmf") { addSubLine(calcCMF(candles,p.period),color); }
        else if (def.type==="stoch") { const {k,d}=calcStoch(candles,p.k,p.d); addSubLine(k,"#3b82f6"); addSubLine(d,"#f59e0b"); }
        else if (def.type==="stochrsi") { const {k,d}=calcStochRSI(candles,p.rsi,p.stoch,p.k,p.d); addSubLine(k,"#38bdf8",2); addSubLine(d,"#f59e0b",1); }
        else if (def.type==="chop") { addSubLine(calcChoppinessIndex(candles,p.period),color,2); }
        else if (def.type==="volume") {
          const s=subChart.addSeries(HistogramSeries,{priceLineVisible:false,lastValueVisible:false});
          s.setData(calcVolume(candles)); 
        }
        else if (def.type==="macd") {
          const {macd,signal,hist}=calcMACD(candles,p.fast,p.slow,p.signal);
          const hs=subChart.addSeries(HistogramSeries,{priceLineVisible:false,lastValueVisible:false});
          hs.setData(hist.map(d=>({...d,color:d.value>=0?"#00d4aa88":"#ef444488"})));
          addSubLine(macd,"#3b82f6"); addSubLine(signal,"#f59e0b");
        }
        } catch(e) { console.warn(`Sub indicator ${id} (${def.type}) failed:`, e); }
      });
    }
    // ── Draw custom formula indicators ────────────────────────────
    for (const ci of customInds) {
      try {
        const pts = evalCustomFormula(ci.formula, candles);
        if (!pts || pts.length === 0) continue;
        if (ci.pane === "main") {
          const s = chart.addSeries(LineSeries, { color: ci.color, lineWidth: 2, priceLineVisible: false, lastValueVisible: true, title: ci.label });
          s.setData(pts.filter(p => p && p.time && isFinite(p.value)));
          customSeriesRefs.current.push(s);
        }
        // Sub-pane custom indicators rendered after subChart is created (see below)
      } catch {}
    }
    // ── Render strategy indicator lines ───────────────────────────
    if (strategyIndicators && Array.isArray(strategyIndicators)) {
      strategyIndicators.forEach(ind => {
        if (!ind.data || !ind.data.length) return;
        try {
          const line = chart.addSeries(LineSeries, { color: ind.color || '#f59e0b', lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: true, title: ind.name || '' });
          line.setData(ind.data);
        } catch(e) {}
      });
    }

    // ── Render volume profile ─────────────────────────────────────
    if (volumeProfile && volumeProfile.length > 0) {
      const maxVolume = Math.max(...volumeProfile.map(p => p.volume));
      volumeProfile.forEach(profile => {
        if (profile.volume > maxVolume * 0.1) { // Only show significant volume levels
          try {
            const volumeBar = chart.addSeries(HistogramSeries, {
              color: profile.volume > maxVolume * 0.3 ? '#fbbf24' : '#64748b',
              priceFormat: { type: 'price', precision: 2 },
              scaleMargins: { top: 0.7, bottom: 0 },
            });
            volumeBar.setData([{
              time: candles[candles.length - 1]?.time || Date.now() / 1000,
              value: profile.price,
              color: profile.volume > maxVolume * 0.3 ? '#fbbf24' : '#64748b'
            }]);
            customSeriesRefs.current.push(volumeBar);
          } catch(e) {}
        }
      });
    }
  }, [activeInds, customInds, strategyIndicators, data?.candles, getSorted, volumeProfile]);

  // ── Re-render drawings after indicator refresh ──────────────────────────
  useEffect(() => {
    if (drawingsRef.current.length > 0) {
      renderDrawings(chartRef.current, seriesRef.current, drawingsRef.current);
    }
  }, [activeInds, customInds, strategyIndicators, data?.candles, getSorted, volumeProfile, renderDrawings]);

  // ── Also re-render when drawings state changes ──────────────────────────
  useEffect(() => {
    renderDrawings(chartRef.current, seriesRef.current, drawings);
  }, [drawings, renderDrawings]);

  // ── P1 SESSION DIVIDERS — vertical markers at session boundaries ────────
  // lightweight-charts can't do true vertical background bands natively, so we use:
  //   1) Two thin horizontal price lines (top 25% + bottom 25% of visible range) at each boundary time,
  //      giving a "tick mark" effect at every session start.
  //   2) HTML overlay labels (rendered separately) that float at the top of the chart.
  // Times are derived from the visible candle range — only draw dividers that fall within loaded data.
  // Session hours are in UTC. Each session: SYDNEY=22, TOKYO=00, LONDON=07(UK DST) or 08, NY=12(US DST) or 13.
  useEffect(() => {
    const chart = chartRef.current, series = seriesRef.current;
    if (!chart || !series) return;
    // cleanup previous — entries are {type:'priceLine'|'series', handle}
    sessionMarkersRef.current.forEach(entry => {
      try {
        if (entry.type === 'priceLine') series.removePriceLine(entry.handle);
        else if (entry.type === 'series') chart.removeSeries(entry.handle);
      } catch {}
    });
    sessionMarkersRef.current = [];
    if (!showSessions) return;
    const candles = getSorted();
    if (!candles.length) return;
    const minTime = candles[0].time;
    const maxTime = candles[candles.length - 1].time;
    // Determine session start hours (UTC) — fixed at standard times for clarity.
    // DST would shift LONDON/NY by 1h, but for visual purposes we use the canonical boundaries.
    const sessionBounds = [
      { name: "TOKYO",    hour:  0, color: "#f59e0b" },
      { name: "LONDON",   hour:  8, color: "#a855f7" },
      { name: "NEW YORK", hour: 13, color: "#00d4aa" },
      { name: "SYDNEY",   hour: 22, color: "#06b6d4" },
    ];
    // Build boundary timestamps (UTC seconds) within [minTime, maxTime].
    const startDate = new Date(minTime * 1000);
    startDate.setUTCHours(0, 0, 0, 0);  // midnight UTC of first day
    const boundaries = [];
    for (let t = startDate.getTime() / 1000; t <= maxTime + 86400; t += 86400) {
      sessionBounds.forEach(b => {
        const bt = t + b.hour * 3600;
        if (bt >= minTime && bt <= maxTime) boundaries.push({ time: bt, ...b });
      });
    }
    // Get visible price range to place top/bottom tick marks.
    const priceRange = (() => {
      try {
        const ps = chart.priceScale('right');
        const coords = ps.getCoordinateToPriceFactor ? null : null;
        // approximate: use min/max of recent candles
        const recent = candles.slice(-50);
        const minP = Math.min(...recent.map(c => c.low));
        const maxP = Math.max(...recent.map(c => c.high));
        return { min: minP, max: maxP };
      } catch { return { min: 0, max: 1 }; }
    })();
    const top = priceRange.min + (priceRange.max - priceRange.min) * 0.97;
    const bot = priceRange.min + (priceRange.max - priceRange.min) * 0.03;
    boundaries.forEach(b => {
      try {
        // 2 horizontal price lines act as tick marks at top + bottom of chart
        const topLine = series.createPriceLine({
          price: top, color: b.color, lineWidth: 1, lineStyle: 3, axisLabelVisible: false,
        });
        const botLine = series.createPriceLine({
          price: bot, color: b.color, lineWidth: 1, lineStyle: 3, axisLabelVisible: false,
        });
        sessionMarkersRef.current.push({type:'priceLine', handle:topLine}, {type:'priceLine', handle:botLine});
      } catch {}
    });
    // Also draw a true diagonal LineSeries from (boundary, top) to (boundary+60, top) — gives a subtle vertical hint
    boundaries.forEach(b => {
      try {
        const s = chart.addSeries(LineSeries, {
          color: b.color, lineWidth: 2, lineStyle: 2,
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        s.setData([{ time: b.time, value: bot }, { time: b.time + 60, value: top }]);
        sessionMarkersRef.current.push({type:'series', handle:s});
      } catch {}
    });
  }, [showSessions, data?.candles?.length, getSorted]);  // candles LENGTH not array — avoid thrash on every tick

  // ── Fullscreen ────────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(() => {
    if (!wrapperRef.current) return;
    if (!document.fullscreenElement) {
      wrapperRef.current.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);
  // 2026-10-09 agent-ui: refresh latest values for imperative agent methods (no re-render cost)
  agentLiveRef.current = {
    toggleFullscreen, renderDrawings, refreshPresetList, symbol, timeframe, data, lastPrice,
    activeInds, customInds, candleColors, showSessions, showHourHeatmap, showHistoryMarkers,
    showActiveMarkers, showStrategyInd, showEquityCurve, mtfPanel, riskModal, linkMode,
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);

  
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── Zoom ──────────────────────────────────────────────────────────────
  const zoomIn  = useCallback(() => { const ts=chartRef.current?.timeScale(); if(!ts)return; const {from,to}=ts.getVisibleLogicalRange()||{}; if(from==null)return; const mid=(from+to)/2,r=(to-from)*0.65; ts.setVisibleLogicalRange({from:mid-r/2,to:mid+r/2}); },[]);
  const zoomOut = useCallback(() => { const ts=chartRef.current?.timeScale(); if(!ts)return; const {from,to}=ts.getVisibleLogicalRange()||{}; if(from==null)return; const mid=(from+to)/2,r=(to-from)*1.5; ts.setVisibleLogicalRange({from:mid-r/2,to:mid+r/2}); },[]);
  const fitAll  = useCallback(() => chartRef.current?.timeScale().fitContent(), []);

  const toggleInd = (id) => {
    setActiveInds(prev => {
      if (prev[id]) { const n={...prev}; delete n[id]; return n; }
      const def = INDICATOR_CATALOGUE.find(i => i.id === id);
      return { ...prev, [id]: { ...def } };
    });
  };
  const updateParam = (id, key, val) => setActiveInds(prev => prev[id] ? { ...prev, [id]: { ...prev[id], params: { ...prev[id].params, [key]: val } } } : prev);
  const updateColor = (id, color) => setActiveInds(prev => prev[id] ? { ...prev, [id]: { ...prev[id], color } } : prev);

  const addCustomIndicator = () => {
    setCustomError("");
    if (!customFormula.trim()) return setCustomError("Enter a formula");
    const candles = getSorted();
    const test = evalCustomFormula(customFormula.trim(), candles);
    if (!test || test.length === 0) return setCustomError("Formula returned no data. Check syntax.");
    const id = "custom_" + Date.now();
    setCustomInds(prev => [...prev, { id, formula: customFormula.trim(), label: customLabel || customFormula.trim(), color: customColor, pane: customPane }]);
    setCustomFormula(""); setCustomLabel("");
  };
  const removeCustomInd = (id) => setCustomInds(prev => prev.filter(c => c.id !== id));

  // ── P0.2 Fetch historical trades for this symbol (for marker overlay) ────
  // Pulls last 50 closed WIN/LOSS trades from /api/data/trades. Re-fetches when
  // symbol changes or every 5 min (lightweight, no websocket needed).
  useEffect(() => {
    if (!symbol) return;
    const last = lastHistoryFetchRef.current;
    if (last.sym === symbol && Date.now() - last.ts < 5 * 60 * 1000) return;
    lastHistoryFetchRef.current = { sym: symbol, ts: Date.now() };
    const token = (() => { try { return localStorage.getItem("kara_auth_token"); } catch { return null; } })();
    const username = (() => { try { const u = JSON.parse(localStorage.getItem("kara_user") || "{}"); return u.username || u.display_name || ""; } catch { return ""; } })();
    const url = `/api/data/trades?${username ? `username=${encodeURIComponent(username)}&` : ""}symbol=${encodeURIComponent(symbol)}&limit=50`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : { trades: [] })
      .then(d => {
        const rows = (d.trades || [])
          .filter(t => t.outcome === "WIN" || t.outcome === "LOSS")
          .filter(t => t.timestamp)
          .map(t => ({
            id: t.id || t.contract_id || `${t.timestamp}-${t.symbol}`,
            time: Math.floor(Number(t.timestamp) / 1000),  // lightweight-charts uses SECONDS
            outcome: t.outcome,
            pnl: Number(t.pnl) || 0,
            strategy: t.strategy || "",
            entry_price: Number(t.entry_price) || null,
            exit_price: Number(t.exit_price) || null,
            direction: t.direction || (Number(t.pnl) >= 0 ? "CALL" : "PUT"),
            symbol: t.symbol,
          }))
          .sort((a, b) => a.time - b.time);  // setMarkers requires ASCENDING time
        setHistoricalTrades(rows);
      })
      .catch(() => { /* silent — historical markers are non-critical */ });
  }, [symbol]);

  // ── P1 Fetch hourly win-rate stats for hour heatmap ──────────────────────
  // Same symbol + 5-min dedupe. Backend: /api/analytics/hourly?symbol=R_50 (returns hour_utc + win_rate_pct + pnl per hour)
  useEffect(() => {
    if (!symbol) return;
    const last = lastHourlyFetchRef.current;
    if (last.sym === symbol && Date.now() - last.ts < 5 * 60 * 1000) return;
    lastHourlyFetchRef.current = { sym: symbol, ts: Date.now() };
    const token = (() => { try { return localStorage.getItem("kara_auth_token"); } catch { return null; } })();
    const username = (() => { try { const u = JSON.parse(localStorage.getItem("kara_user") || "{}"); return u.username || u.display_name || ""; } catch { return ""; } })();
    const url = `/api/analytics/hourly?${username ? `username=${encodeURIComponent(username)}&` : ""}symbol=${encodeURIComponent(symbol)}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : { hourly: [] })
      .then(d => {
        const map = {};
        (d.hourly || []).forEach(row => {
          const h = Number(row.hour_utc);
          if (!Number.isFinite(h) || h < 0 || h > 23) return;
          map[h] = {
            trades: Number(row.trades) || 0,
            wins: Number(row.wins) || 0,
            losses: Number(row.losses) || 0,
            pnl: Number(row.pnl) || 0,
            win_rate_pct: Number(row.win_rate_pct) || 0,
            session: row.session || null,
          };
        });
        setHourlyStats(map);
      })
      .catch(() => { /* silent */ });
  }, [symbol]);

  // ── Trade entry markers (active + historical merged) ──────────────────────
  useEffect(() => {
    if (!seriesRef.current) return;
    const markers = [];
    if (showActiveMarkers && activeTrades.length) {
      activeTrades
        .filter(t => t.purchase_time)
        .forEach(t => {
          // Reconcile: if we have pending Date.now()-keyed lines for this trade, re-key to contract_id
          const contractKey = String(t.contract_id || t.id || "");
          if (contractKey) {
            Object.keys(tradeLinesRef.current).forEach(k => {
              if (k === "mlPrediction") return;
              if (k !== contractKey && /^\d+$/.test(k) && tradeLinesRef.current[k]) {
                const lines = tradeLinesRef.current[k];
                const ageMs = Date.now() - Number(k);
                // If this pending line set is recent (< 30s old) and we have an active trade without lines yet, adopt it
                if (ageMs < 30000 && !tradeLinesRef.current[contractKey]) {
                  tradeLinesRef.current[contractKey] = lines;
                  delete tradeLinesRef.current[k];
                }
              }
            });
          }
          markers.push({
            time: t.purchase_time,
            position: t.contract_type === "CALL" ? "belowBar" : "aboveBar",
            color:     t.contract_type === "CALL" ? "#00d4aa"  : "#ef4444",
            shape:     t.contract_type === "CALL" ? "arrowUp"  : "arrowDown",
            text:      `${t.contract_type} $${t.buy_price?.toFixed(2)}`,
          });
        });
    }
    if (showHistoryMarkers && historicalTrades.length) {
      historicalTrades.forEach(t => {
        const isWin = t.outcome === "WIN";
        const pnlSign = t.pnl >= 0 ? "+" : "";
        markers.push({
          time: t.time,
          position: isWin ? "belowBar" : "aboveBar",
          color:     isWin ? "#00d4aa" : "#ef4444",
          shape:     isWin ? "arrowUp"  : "arrowDown",
          text:      `${isWin ? "✓" : "✗"} ${pnlSign}${t.pnl.toFixed(2)}`,
        });
      });
    }
    if (signalMarkers.length) {
      signalMarkers.forEach(m => markers.push(m));
    }
    markers.sort((a, b) => a.time - b.time);  // lightweight-charts requires ASC time
    try {
      if (seriesRef.current) {
        if (!markersPrimitiveRef.current) {
          markersPrimitiveRef.current = createSeriesMarkers(seriesRef.current, markers);
        } else {
          markersPrimitiveRef.current.setMarkers(markers);
        }
      }
    } catch (e) {
      console.warn("[ChartWidget] setMarkers error:", e);
    }
  }, [activeTrades, historicalTrades, signalMarkers, showHistoryMarkers, showActiveMarkers]);

  // ── P0.1 Cleanup trade price lines when trades close ──────────────────────
  // When a trade disappears from activeTrades, remove its entry/TP/SL price lines.
  // Tracks contract_id → set of priceLines via tradeLinesRef.
  useEffect(() => {
    if (!seriesRef.current) return;
    const activeContractIds = new Set(activeTrades.map(t => String(t.contract_id || t.id || "")));
    Object.keys(tradeLinesRef.current).forEach(tradeId => {
      if (tradeId === "mlPrediction") return;  // skip ML prediction lines
      if (!activeContractIds.has(String(tradeId))) {
        const lines = tradeLinesRef.current[tradeId];
        if (Array.isArray(lines)) {
          lines.forEach(line => { try { seriesRef.current.removePriceLine(line); } catch {} });
        }
        delete tradeLinesRef.current[tradeId];
      }
    });
  }, [activeTrades]);

  // ── MT5-Style Active Order/Position lines (Green for Buy, Red for Sell, TP/SL dotted) ──
  useEffect(() => {
    if (!seriesRef.current) return;
    activeTrades.forEach(t => {
      const contractKey = String(t.contract_id || t.id || "");
      if (!contractKey || tradeLinesRef.current[contractKey]) return;
      
      const entryPrice = t.barrier || t.entry_spot || t.entry || t.buy_price;
      if (!entryPrice || !isFinite(entryPrice)) return;
      
      const isCall = t.contract_type === "CALL" || t.direction === "CALL" || t.type === "BUY";
      const lines = [];
      try {
        const entryLine = seriesRef.current.createPriceLine({
          price: Number(entryPrice),
          color: isCall ? '#10b981' : '#ef4444',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: `MT5 ${isCall ? 'BUY' : 'SELL'} #${contractKey.slice(-4)}`,
        });
        lines.push(entryLine);

        if (t.tp) {
          const tpLine = seriesRef.current.createPriceLine({
            price: Number(t.tp),
            color: '#22c55e',
            lineWidth: 1,
            lineStyle: 1,
            axisLabelVisible: true,
            title: `TP #${contractKey.slice(-4)}`,
          });
          lines.push(tpLine);
        }
        if (t.sl) {
          const slLine = seriesRef.current.createPriceLine({
            price: Number(t.sl),
            color: '#dc2626',
            lineWidth: 1,
            lineStyle: 1,
            axisLabelVisible: true,
            title: `SL #${contractKey.slice(-4)}`,
          });
          lines.push(slLine);
        }
        tradeLinesRef.current[contractKey] = lines;
      } catch (e) {
        console.warn("[ChartWidget] Error drawing MT5 trade lines:", e);
      }
    });
  }, [activeTrades]);

  // TradingView/MT5 1-second ticker for Bar Countdown & trade expiry
  const [currentNowSec, setCurrentNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentNowSec(Math.floor(Date.now() / 1000));
      forceRender(n => n + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const getTfSeconds = (tf) => {
    if (!tf) return 60;
    const s = String(tf).toLowerCase();
    if (s.includes("1m") || s === "1" || s === "m1") return 60;
    if (s.includes("2m") || s === "2" || s === "m2") return 120;
    if (s.includes("3m") || s === "3" || s === "m3") return 180;
    if (s.includes("5m") || s === "5" || s === "m5") return 300;
    if (s.includes("15m") || s === "15" || s === "m15") return 900;
    if (s.includes("30m") || s === "30" || s === "m30") return 1800;
    if (s.includes("1h") || s === "60" || s === "h1") return 3600;
    if (s.includes("4h") || s === "240" || s === "h4") return 14400;
    if (s.includes("1d") || s === "d") return 86400;
    return 60;
  };

  const hasSubPane = Object.keys(activeInds).some(id => INDICATOR_CATALOGUE.find(i=>i.id===id)?.pane==="sub");
  const activeCount = Object.keys(activeInds).length + customInds.length;

  const candleList = Array.isArray(data) ? data : data?.candles;
  if (!candleList || candleList.length === 0)
    return <div style={{background:"#0d1f35",border:"1px solid #1e3a5f",borderRadius:8,height,display:"flex",alignItems:"center",justifyContent:"center",color:"#64748b",marginBottom:14}}>Waiting for {symbol} data…</div>;

  // Handle trade execution from modal
  const executeTradeFromModal = (direction) => {
    const payload = {
      symbol,
      direction,
      entry: tradeForm.entry,
      tp1: tradeForm.tp1,
      tp2: tradeForm.tp2,
      sl: tradeForm.sl,
      stake: tradeForm.stake,
      duration: tradeForm.duration,
      duration_unit: tradeForm.duration_unit
    };
    const callback = direction === "CALL" ? onBuy : onSell;
    if (callback) callback(payload);

    // Draw price lines on chart
    if (seriesRef.current && chartRef.current) {
      const tradeId = `${Date.now()}`;
      const lines = [];
      try {
        // Entry line (blue)
        if (tradeForm.entry) {
          const line = seriesRef.current.createPriceLine({
            price: tradeForm.entry,
            color: '#3b82f6',
            lineWidth: 1,
            lineStyle: 0,
            axisLabelVisible: true,
            title: 'Entry',
          });
          lines.push(line);
        }
        // TP1 (green)
        if (tradeForm.tp1) {
          const line = seriesRef.current.createPriceLine({
            price: tradeForm.tp1,
            color: '#00d4aa',
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: 'TP1',
          });
          lines.push(line);
        }
        // TP2 (brighter green)
        if (tradeForm.tp2) {
          const line = seriesRef.current.createPriceLine({
            price: tradeForm.tp2,
            color: '#10b981',
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: 'TP2',
          });
          lines.push(line);
        }
        // SL (red)
        if (tradeForm.sl) {
          const line = seriesRef.current.createPriceLine({
            price: tradeForm.sl,
            color: '#ef4444',
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: 'SL',
          });
          lines.push(line);
        }
        tradeLinesRef.current[tradeId] = lines;
      } catch(e) { console.error("Error drawing price lines:", e); }
    }

    setTradeModal(null);
  };

  const tfSec = getTfSeconds(timeframe);
  const curList = Array.isArray(data) ? data : data?.candles;
  const lastC = curList && curList.length ? curList[curList.length - 1] : null;
  const lastCTime = lastC ? (lastC.time > 1e11 ? Math.floor(lastC.time / 1000) : lastC.time) : 0;
  let barCountdownSec = 0;
  if (lastCTime > 0 && currentNowSec >= lastCTime) {
    const cClose = lastCTime + tfSec;
    barCountdownSec = currentNowSec < cClose ? (cClose - currentNowSec) : (tfSec - (currentNowSec % tfSec));
  } else {
    barCountdownSec = tfSec - (currentNowSec % tfSec);
  }
  const formatCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    if (sec >= 3600) {
      const h = Math.floor(sec / 3600);
      const remM = Math.floor((sec % 3600) / 60);
      return `${String(h).padStart(2,'0')}:${String(remM).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  const estSpread = (() => {
    if (data?.spread != null) return `${Number(data.spread).toFixed(2)} pts`;
    if (data?.ask && data?.bid) return `${Number(data.ask - data.bid).toFixed(2)} pts`;
    const p = lastPrice || (lastC ? lastC.close : 0);
    if (!p) return "0.00 pts";
    const spreadVal = Math.max(0.01, p * 0.0003);
    return `${spreadVal.toFixed(p > 100 ? 2 : 4)} pts`;
  })();

  return (
    <div ref={wrapperRef} style={{background:"#0d1f35",border:"1px solid #1e3a5f",borderRadius:8,overflow:"hidden",marginBottom:14}}>

      {/* ── Trade Modal ── */}
      {tradeModal && (
        <div onClick={()=>setTradeModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:50000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#0a1628",border:`2px solid ${tradeModal.direction==="CALL"?"#00d4aa":"#ef4444"}`,borderRadius:12,padding:28,width:"90%",maxWidth:420,fontFamily:"'Courier New',monospace",boxShadow:`0 0 60px ${tradeModal.direction==="CALL"?"#00d4aa22":"#ef444422"}`}}>
            <div style={{fontSize:14,color:"#f59e0b",letterSpacing:2,marginBottom:6}}>🚨 TRADE SETUP</div>
            <div style={{fontSize:24,fontWeight:900,color:tradeModal.direction==="CALL"?"#00d4aa":"#ef4444",marginBottom:20}}>{tradeModal.direction==="CALL"?"📈 BUY":"📉 SELL"} {symbol}</div>

            {/* Form fields */}
            <div style={{display:"flex",flexDirection:"column",gap:14,marginBottom:20}}>
              {[
                {label:"Entry Price",field:"entry"},
                {label:"Stop Loss",field:"sl"},
                {label:"Take Profit 1",field:"tp1"},
                {label:"Take Profit 2",field:"tp2"},
              ].map(({label,field})=>(
                <div key={field}>
                  <label style={{fontSize:11,color:"#94a3b8",display:"block",marginBottom:4}}>{label}</label>
                  <input type="number" step="0.01" value={tradeForm[field]||""} onChange={e=>setTradeForm({...tradeForm,[field]:parseFloat(e.target.value)||null})}
                    style={{width:"100%",padding:"8px 10px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12,boxSizing:"border-box"}}/>
                </div>
              ))}

              {/* Quick stake presets */}
              <div>
                <label style={{fontSize:11,color:"#94a3b8",display:"block",marginBottom:4}}>Stake (USD)</label>
                <div style={{display:"flex",gap:4,marginBottom:6}}>
                  {[1,5,10,25,50].map(preset=>(
                    <button key={preset} onClick={()=>setTradeForm({...tradeForm,stake:preset})}
                      style={{flex:1,padding:"5px 0",borderRadius:3,
                        border:`1px solid ${tradeForm.stake===preset?"#3b82f6":"#1e3a5f"}`,
                        background:tradeForm.stake===preset?"#1e3a5f":"transparent",
                        color:tradeForm.stake===preset?"#93c5fd":"#64748b",
                        fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
                      ${preset}
                    </button>
                  ))}
                </div>
                <input type="number" step="0.01" value={tradeForm.stake||""}
                  onChange={e=>setTradeForm({...tradeForm,stake:parseFloat(e.target.value)||null})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:4,background:"#0f1c2e",
                    border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12,boxSizing:"border-box"}}/>
              </div>

              {/* R/R ratio display */}
              {tradeForm.entry && tradeForm.tp1 && tradeForm.sl && (()=>{
                const tpDist = Math.abs(tradeForm.tp1 - tradeForm.entry);
                const slDist = Math.abs(tradeForm.sl - tradeForm.entry);
                const rr = slDist > 0 ? (tpDist / slDist).toFixed(2) : null;
                const rrColor = rr >= 2 ? "#00d4aa" : rr >= 1.5 ? "#f59e0b" : "#ef4444";
                return (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"8px 10px",background:"#0f1c2e",borderRadius:4,border:"1px solid #1e3a5f"}}>
                    <span style={{fontSize:11,color:"#94a3b8"}}>R/R Ratio</span>
                    <span style={{fontSize:14,fontWeight:900,color:rrColor,fontFamily:"'Courier New',monospace"}}>
                      {rr ? `1:${rr}` : "—"}
                    </span>
                  </div>
                );
              })()}

              {/* Duration */}
              <div>
                <label style={{fontSize:11,color:"#94a3b8",display:"block",marginBottom:4}}>Duration</label>
                <div style={{display:"flex",gap:8}}>
                  <input type="number" min="1" value={tradeForm.duration} onChange={e=>setTradeForm({...tradeForm,duration:e.target.value})}
                    style={{flex:1,padding:"8px 10px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12}}/>
                  <select value={tradeForm.duration_unit} onChange={e=>setTradeForm({...tradeForm,duration_unit:e.target.value})}
                    style={{padding:"8px 10px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12}}>
                    <option value="m">minutes</option>
                    <option value="t">ticks</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>executeTradeFromModal(tradeModal.direction)} style={{flex:1,padding:"12px 16px",borderRadius:6,background:tradeModal.direction==="CALL"?"#00d4aa":"#ef4444",color:tradeModal.direction==="CALL"?"#000":"#fff",border:"none",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                {tradeModal.direction==="CALL"?"▲ EXECUTE BUY":"▼ EXECUTE SELL"}
              </button>
              <button onClick={()=>setTradeModal(null)} style={{flex:1,padding:"12px 16px",borderRadius:6,background:"#1e3a5f",color:"#94a3b8",border:"none",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toolbar (P3 mobile-aware 2026-08-31) ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 12px",borderBottom:"1px solid #1e3a5f",background:"#0a1628",flexWrap:"wrap",gap:5,position:"relative"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",flex:1,minWidth:0}}>
          <span style={{fontSize:12,color:"#64748b",fontWeight:700,letterSpacing:1}}>📊 {symbol}</span>
          <button onClick={toggleFullscreen} title={isFullscreen?"Exit fullscreen":"Go fullscreen"}
            style={{background:"#0d1f35",color:"#e2e8f0",border:"1px solid #1e3a5f",borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            {isFullscreen ? "\u229E Exit Fullscreen" : "\u26F6 Fullscreen"}
          </button>
          <ChartVoiceButton
            symbol={symbol}
            onRunAnalyze={onRunAnalyze}
            onRunFindTrade={onRunFindTrade}
            onTrade={onTrade}
            onCloseSymbol={onCloseSymbol}
            onCloseAll={onCloseAll}
            onClearIndicators={onClearIndicators}
            onSpeak={onSpeak}
            onSetSymbol={onSetSymbol}
            onSetTimeframe={onSetTimeframe}
            voiceRef={ref}
          />
          <button onClick={() => setShowColorPicker(!showColorPicker)} title="Customize colors" style={{padding:"6px 13px",background:showColorPicker?"#1e3a5f":"#0f1c2e",color:showColorPicker?"#fbbf24":"#64748b",border:"none",borderRadius:3,cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700}}>🎨 Colors</button>
          {onMlPredict && (
            <button 
              onClick={async () => {
                setMlPredictionOverlay({ loading: true });
                const res = await onMlPredict(symbol);
                setMlPredictionOverlay(res);
                // P2-2: badge update on every ML prediction so trader sees fresh confidence
                try {
                  if (res && !res.error) {
                    const conf = Number(res.confidence || res.win_probability || 0);
                    setMlBadgeFromPrediction(symbol, res.prediction || res.direction || "NONE", conf);
                  }
                } catch {}
                
                // Draw prediction on chart
                if (res && !res.error && seriesRef.current && chartRef.current) {
                  const lastCandle = data?.candles?.[data.candles.length-1];
                  if (lastCandle) {
                    const predictionColor = res.prediction === "CALL" ? "#00d4aa" : "#ef4444";
                    const confidence = res.confidence || res.win_probability || 0;
                    
                    // Draw prediction target line
                    const targetPrice = res.prediction === "CALL" 
                      ? lastCandle.close * (1 + confidence * 0.02) // 2% max move based on confidence
                      : lastCandle.close * (1 - confidence * 0.02);
                    
                    try {
                      const predictionLine = seriesRef.current.createPriceLine({
                        price: targetPrice,
                        color: predictionColor,
                        lineWidth: 2,
                        lineStyle: 2, // dashed
                        axisLabelVisible: true,
                        title: `ML ${res.prediction} ${(confidence*100).toFixed(0)}%`,
                      });
                      
                      // Store the line for cleanup
                      if (!tradeLinesRef.current.mlPrediction) {
                        tradeLinesRef.current.mlPrediction = [];
                      }
                      tradeLinesRef.current.mlPrediction.push(predictionLine);
                      
                      // Auto-remove after 5 minutes
                      setTimeout(() => {
                        try {
                          chartRef.current?.removePriceLine(predictionLine);
                          tradeLinesRef.current.mlPrediction = tradeLinesRef.current.mlPrediction?.filter(l => l !== predictionLine);
                        } catch {}
                      }, 5 * 60 * 1000);
                      
                    } catch(e) {
                      console.error("Error drawing ML prediction line:", e);
                    }
                  }
                }
              }} 
              title="Get ML prediction and draw on chart"
              style={{background:"#1a0a2e",color:"#a855f7",border:"1px solid #a855f744",borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
              {"\uD83E\uDD16"} ML Predict
            </button>
          )}
          {/* P3 mobile-toolbar: hide secondary buttons behind "⋮ More" drawer on mobile */}
          {isMobile && (
            <button onClick={() => setShowMoreMenu(v => !v)} title="Show more toolbar buttons"
              data-testid="more-toolbar-button"
              style={{background:showMoreMenu?"#1e3a5f":"#0d1f35",color:showMoreMenu?"#fbbf24":"#94a3b8",border:"1px solid #1e3a5f",borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
              {showMoreMenu ? "✕ Close" : "⋮ More"}
            </button>
          )}
          {/* P3 mobile-toolbar drawer for secondary buttons (Volume Profile → Hour Heat) */}
          {(isMobile ? showMoreMenu : true) && (
          <div data-testid="more-toolbar-drawer" style={isMobile ? {display:"flex",flexDirection:"column",gap:4,width:"100%",padding:"6px 0",borderTop:"1px solid #1e3a5f",marginTop:4} : {display:"contents"}}>
          <button onClick={() => {
            // Toggle volume profile
            if (volumeProfile) {
              setVolumeProfile(null);
            } else {
              const profile = calcVolumeProfile(data?.candles || []);
              setVolumeProfile(profile);
            }
          }} title="Toggle Volume Profile"
            style={{background:volumeProfile?"#1e3a5f":"#0d1f35",color:volumeProfile?"#fbbf24":"#64748b",border:`1px solid ${volumeProfile?"#fbbf2444":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            📊 Volume Profile
          </button>
          <button onClick={() => setAlertModal({})} title="Set Price Alerts"
            style={{background:"#0d1f35",color:"#f59e0b",border:"1px solid #f59e0b44",borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            🔔 Price Alerts ({priceAlerts.length})
          </button>
          <button onClick={() => setDrawingModal(v => !v)} title="Drawing Tools"
            style={{background:drawingMode?"#1a0a2e":"#0d1f35",color:drawingMode?"#8b5cf6":"#8b5cf6",border:`1px solid ${drawingMode?"#8b5cf6":"#8b5cf644"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            ✏️ Drawing Tools {drawingMode ? `(${drawingMode})` : drawings.length > 0 ? `(${drawings.length})` : ''}
          </button>
          {/* P1 #6 layout presets — save / load named chart setups */}
          <button onClick={() => { setPresetModal("load"); refreshPresetList(); }} title="Save/Load chart setup presets (voice: 'save setup scalper-1' / 'load scalper-1')"
            data-testid="preset-button"
            style={{background:presetModal?"#0a2e1a":"#0d1f35",color:presetModal?"#10b981":"#64748b",border:`1px solid ${presetModal?"#10b98155":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            💾 Setups {presetList.length > 0 ? `(${presetList.length})` : ''}
          </button>
          {/* P1 #5 per-symbol risk score panel — voice: "how risky is R_25?" */}
          <button onClick={() => { setRiskModal(v => !v); if (!riskModal) fetchRiskScores(); }} title="Per-symbol risk score from live trades (voice: 'how risky is R_25?')"
            data-testid="risk-button"
            style={{background:riskModal?"#2e0a0a":"#0d1f35",color:riskModal?"#ef4444":"#64748b",border:`1px solid ${riskModal?"#ef444455":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            📊 Risk {riskData?.risk ? `(${riskData.risk.score.total})` : riskData?.risks ? `(${riskData.risks.length})` : ''}
          </button>
          {/* P2 #8 chart screenshot — uses native lightweight-charts takeScreenshot(), zero deps */}
          <button onClick={handleScreenshot} title="Download chart as PNG (voice: 'screenshot this')"
            data-testid="screenshot-button"
            style={{background:"#0d1f35",color:"#94a3b8",border:"1px solid #1e3a5f",borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            📸 Shot
          </button>
          {/* P1 #4 multi-timeframe verdict — voice: "show me MTF" */}
          <button onClick={() => handleToggleMtf()} title="Multi-timeframe verdict (voice: 'show me MTF' / 'mtf for R_25')"
            data-testid="mtf-button"
            style={{background:mtfPanel?"#0a2e1a":"#0d1f35",color:mtfPanel?"#10b981":"#64748b",border:`1px solid ${mtfPanel?"#10b98155":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            🧭 MTF
          </button>
          {/* P2 #13 equity curve — paints BaselineSeries into the sub-pane */}
          <button onClick={() => setShowEquityCurve(s => !s)} title="Show running P&L equity curve (voice: 'show equity')"
            data-testid="equity-button"
            style={{background:showEquityCurve?"#0a2e1a":"#0d1f35",color:showEquityCurve?"#10b981":"#64748b",border:`1px solid ${showEquityCurve?"#10b98155":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            💰 Equity {equitySummary ? `(${equitySummary.total_pnl >= 0 ? "+" : ""}$${equitySummary.total_pnl.toFixed(0)})` : ""}
          </button>
          {/* P0.2 marker layer toggles */}
          <button onClick={() => setShowHistoryMarkers(s => !s)} title="Toggle historical trade markers"
            data-testid="toggle-history-markers"
            style={{background:showHistoryMarkers?"#0a2e1a":"#0d1f35",color:showHistoryMarkers?"#00d4aa":"#64748b",border:`1px solid ${showHistoryMarkers?"#00d4aa55":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            📜 History {showHistoryMarkers ? `(${historicalTrades.length})` : ''}
          </button>
          <button onClick={() => setShowActiveMarkers(s => !s)} title="Toggle active trade markers"
            style={{background:showActiveMarkers?"#1a0a2e":"#0d1f35",color:showActiveMarkers?"#f59e0b":"#64748b",border:`1px solid ${showActiveMarkers?"#f59e0b55":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            ⚡ Active {showActiveMarkers ? `(${activeTrades.length})` : ''}
          </button>
          {/* P1 toolbar toggles */}
          <button onClick={() => setShowSessions(s => !s)} title="Toggle session dividers (Sydney/Tokyo/London/NY)"
            style={{background:showSessions?"#1a0a2e":"#0d1f35",color:showSessions?"#a855f7":"#64748b",border:`1px solid ${showSessions?"#a855f755":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            🌐 Sessions
          </button>
          <button onClick={() => setShowHourHeatmap(s => !s)} title="Toggle 24-hour win-rate heatmap"
            style={{background:showHourHeatmap?"#0a2e1a":"#0d1f35",color:showHourHeatmap?"#00d4aa":"#64748b",border:`1px solid ${showHourHeatmap?"#00d4aa55":"#1e3a5f"}`,borderRadius:"4px",padding:"4px 10px",cursor:"pointer",fontSize:"12px",fontWeight:600}}>
            🕐 Hour Heat
          </button>
          </div>
          )}
        </div>
        {/* P3 mobile-toolbar: right-side secondary controls (indicator pills, indicator picker, zoom, timeframe, linked, ML badge, Live) collapse into the More drawer on mobile */}
        <div style={{display: isMobile ? "none" : "flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
          {/* Active pills */}
          {Object.keys(activeInds).map(id => {
            if (["heikin", "tma", "tmabands", "tmacg"].includes(id)) return null; // handled by explicit buttons below
            const def=INDICATOR_CATALOGUE.find(i=>i.id===id); if(!def) return null;
            const color=activeInds[id].color||def.color;
            return <span key={id} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:color+"22",border:`1px solid ${color}55`,color,cursor:"pointer",whiteSpace:"nowrap"}} onClick={()=>toggleInd(id)} title="Click to remove">{def.label} ✕</span>;
          })}
          
          <button onClick={()=>toggleInd("heikin")} style={{...btnS, background: activeInds["heikin"] ? "#1e3a5f" : "#0f1c2e", color: activeInds["heikin"] ? "#4ade80" : "#64748b"}}>HA</button>
          <button onClick={()=>toggleInd("tma")} style={{...btnS, background: activeInds["tma"] ? "#1e3a5f" : "#0f1c2e", color: activeInds["tma"] ? "#38bdf8" : "#64748b"}}>TMA</button>
          <button onClick={()=>toggleInd("tmabands")} style={{...btnS, background: activeInds["tmabands"] ? "#1e3a5f" : "#0f1c2e", color: activeInds["tmabands"] ? "#0ea5e9" : "#64748b"}}>TMA Bands</button>
          <button onClick={()=>toggleInd("tmacg")} style={{...btnS, background: activeInds["tmacg"] ? "#1e3a5f" : "#0f1c2e", color: activeInds["tmacg"] ? "#9333ea" : "#64748b"}}>TMA+CG</button>

          <button onClick={()=>setShowPicker(p=>!p)} style={{...btnS, background:showPicker?"#1e3a5f":"#0f1c2e", color:showPicker?"#93c5fd":activeCount>0?"#f59e0b":"#64748b", border:`1px solid ${showPicker?"#3b82f6":"#1e3a5f"}`}}>
            📈 {activeCount>0?`${activeCount} `:""}Indicators
          </button>
          <button onClick={zoomIn}  style={btnS}>＋</button>
          <button onClick={zoomOut} style={btnS}>－</button>
          <button onClick={fitAll}  style={{...btnS,fontSize:11}}>FIT</button>
          {onSetTimeframe && [["1","1m"],["2","5m"],["3","15m"],["4","1h"],["5","4h"],["6","1d"]].map(([k,lbl]) => (
            <button key={k} onClick={() => { try { onSetTimeframe(lbl); } catch {} }}
              title={"Timeframe " + lbl + " (hotkey " + k + ")"}
              style={{...btnS,fontSize:10,padding:"2px 6px",background:timeframe===lbl?"#1e3a5f":"#0f1c2e",color:timeframe===lbl?"#93c5fd":"#64748b",border:timeframe===lbl?"1px solid #3b82f6":"1px solid #1e3a5f"}}>
              {lbl}
            </button>
          ))}
          <button onClick={() => setLinkMode(v => !v)}
            title={linkMode ? "Unlink charts (hotkey L)" : "Link charts (hotkey L)"}
            style={{...btnS,fontSize:11,background:linkMode?"#1e3a5f":"#0f1c2e",color:linkMode?"#38bdf8":"#64748b",border:linkMode?"1px solid #3b82f6":"1px solid #1e3a5f"}}>
            {linkMode ? "Linked" : "Unlinked"}
          </button>
          <span title={mlBadge.status === "degenerate" ? "ML model is degenerate - recall high but precision very low" : mlBadge.status === "healthy" ? "ML model trained" : "ML service: " + mlBadge.status}
            style={{padding:"2px 7px",borderRadius:4,background:mlBadge.status==="healthy"?"#10b98122":mlBadge.status==="degenerate"?"#ef444422":"#47556922",border:"1px solid " + (mlBadge.status==="healthy"?"#10b981":mlBadge.status==="degenerate"?"#ef4444":"#475569") + "44",color:mlBadge.status==="healthy"?"#10b981":mlBadge.status==="degenerate"?"#ef4444":"#94a3b8",fontSize:10,fontWeight:700,marginLeft:4,fontFamily:"monospace"}}>
            {mlBadge.status==="loading"?"ML...":mlBadge.status==="unreachable"?"ML off":mlBadge.status==="untrained"?"ML n/a":mlBadge.status==="degenerate"?"ML !":(mlBadge.win_rate_train!=null?"ML "+(mlBadge.win_rate_train*100).toFixed(0)+"%":"ML ok")}{mlBadge.last_conf!=null?" "+(mlBadge.last_conf*100).toFixed(0)+"%":""}
          </span>
          <span style={{color:"#00d4aa",fontSize:11,marginLeft:2}}>Live</span>
        </div>
      </div>

      {/* ── Indicator Picker ── */}
      {showPicker && (
        <div style={{background:"#070d1a",borderBottom:"1px solid #1e3a5f",maxHeight:380,overflowY:"auto"}}>
          {/* Category tabs */}
          <div style={{display:"flex",gap:0,borderBottom:"1px solid #1e293b",flexWrap:"wrap"}}>
            {CATEGORIES.map(cat=>(
              <button key={cat} onClick={()=>setPickerCat(cat)}
                style={{padding:"6px 13px",background:pickerCat===cat?"#0f1c2e":"transparent",border:"none",borderBottom:pickerCat===cat?"2px solid #3b82f6":"2px solid transparent",color:pickerCat===cat?"#93c5fd":"#475569",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                {cat}
              </button>
            ))}
          </div>
          {/* Custom indicator builder */}
          {pickerCat === "Custom" && (
            <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,color:"#64748b",lineHeight:1.5}}>
                <b style={{color:"#f59e0b"}}>Formula examples:</b>{" "}
                <code style={{color:"#93c5fd"}}>ema(close,21)</code>{"  "}
                <code style={{color:"#93c5fd"}}>sma(close,50)</code>{"  "}
                <code style={{color:"#93c5fd"}}>rsi(close,7)</code>{"  "}
                <code style={{color:"#93c5fd"}}>atr(14)</code>{"  "}
                <code style={{color:"#93c5fd"}}>bb_upper(20,2)</code>{"  "}
                <code style={{color:"#93c5fd"}}>hma(close,21)</code>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <input value={customFormula} onChange={e=>setCustomFormula(e.target.value)}
                  placeholder="ema(close,21)"
                  style={{flex:2,minWidth:160,padding:"5px 8px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"monospace",fontSize:12}}/>
                <input value={customLabel} onChange={e=>setCustomLabel(e.target.value)}
                  placeholder="Label (optional)"
                  style={{flex:1,minWidth:100,padding:"5px 8px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12}}/>
                <input type="color" value={customColor} onChange={e=>setCustomColor(e.target.value)}
                  style={{width:32,height:32,padding:0,border:"1px solid #1e3a5f",borderRadius:4,background:"transparent",cursor:"pointer"}}/>
                <select value={customPane} onChange={e=>setCustomPane(e.target.value)}
                  style={{padding:"4px 8px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#94a3b8",fontFamily:"inherit",fontSize:12}}>
                  <option value="main">Overlay</option>
                  <option value="sub">Panel</option>
                </select>
                <button onClick={addCustomIndicator}
                  style={{padding:"5px 14px",borderRadius:4,background:"#1e3a5f",border:"1px solid #3b82f6",color:"#93c5fd",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700}}>
                  + Add
                </button>
              </div>
              {customError && <span style={{fontSize:11,color:"#ef4444"}}>{customError}</span>}
              {/* Active custom indicators list */}
              {customInds.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:3,marginTop:4}}>
                  {customInds.map(ci=>(
                    <div key={ci.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 8px",borderRadius:4,background:"#0f1c2e",border:`1px solid ${ci.color}44`}}>
                      <div style={{width:10,height:10,borderRadius:2,background:ci.color,flexShrink:0}}/>
                      <code style={{flex:1,fontSize:11,color:ci.color}}>{ci.label}</code>
                      <span style={{fontSize:10,color:"#475569"}}>{ci.pane==="sub"?"panel":"overlay"}</span>
                      <button onClick={()=>removeCustomInd(ci.id)}
                        style={{padding:"1px 6px",borderRadius:3,background:"transparent",border:"1px solid #ef444466",color:"#ef4444",cursor:"pointer",fontFamily:"inherit",fontSize:11}}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Indicator list for selected category */}
          {pickerCat !== "Custom" && <div style={{padding:"10px 14px",display:"flex",flexDirection:"column",gap:4}}>
            {INDICATOR_CATALOGUE.filter(i=>i.cat===pickerCat).map(def=>{
              const active = !!activeInds[def.id];
              const conf   = activeInds[def.id] || {};
              const color  = conf.color || def.color;
              const params = { ...def.params, ...conf.params };
              const editing = editInd === def.id && active;
              return (
                <div key={def.id} style={{background:active?"#0f1c2e":"transparent",border:`1px solid ${active?color+"44":"#1e293b"}`,borderRadius:6,overflow:"hidden"}}>
                  {/* Row */}
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px"}}>
                    <div onClick={()=>toggleInd(def.id)} style={{width:16,height:16,borderRadius:3,border:`2px solid ${color}`,background:active?color:"transparent",cursor:"pointer",flexShrink:0}}/>
                    <span onClick={()=>toggleInd(def.id)} style={{flex:1,fontSize:12,color:active?color:"#64748b",cursor:"pointer",fontWeight:active?700:400}}>{def.label}</span>
                    <span style={{fontSize:10,color:"#334155",background:"#0a1628",border:"1px solid #1e293b",borderRadius:3,padding:"1px 6px"}}>{def.pane==="sub"?"panel":"overlay"}</span>
                    {active && (
                      <>
                        <input type="color" value={color} onChange={e=>updateColor(def.id,e.target.value)}
                          style={{width:22,height:22,padding:0,border:"1px solid #1e3a5f",borderRadius:3,background:"transparent",cursor:"pointer"}}/>
                        {Object.keys(def.params).length > 0 && (
                          <button onClick={()=>setEditInd(editing?null:def.id)}
                            style={{padding:"2px 8px",borderRadius:3,fontSize:10,background:editing?"#1e3a5f":"#0a1628",border:"1px solid #1e3a5f",color:editing?"#93c5fd":"#475569",cursor:"pointer",fontFamily:"inherit"}}>
                            ⚙ Edit
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {/* Param editor */}
                  {editing && (
                    <div style={{padding:"6px 12px 10px",display:"flex",gap:10,flexWrap:"wrap",borderTop:"1px solid #1e293b",background:"#0a1628"}}>
                      {Object.entries(params).map(([k,v])=>(
                        <label key={k} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#94a3b8"}}>
                          {k}
                          <input type="number" value={v} min={1} max={999} step={def.type==="psar"?0.01:1}
                            onChange={e=>updateParam(def.id, k, parseFloat(e.target.value)||v)}
                            style={{width:60,padding:"3px 6px",borderRadius:3,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#e2e8f0",fontFamily:"inherit",fontSize:12}}/>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      )}

      {/* ── Chart area with overlay ── */}
      <div style={{position:"relative"}}>
        {/* Main chart */}
        <div style={{position:"relative",width:"100%",height}}>
        {crosshairOHLC && (
          <div data-testid="ohlc-legend" style={{position:"absolute",top:6,left:6,zIndex:10,background:"rgba(10,24,40,0.92)",border:"1px solid #1e3a5f",borderRadius:4,padding:"4px 8px",fontSize:11,fontFamily:"monospace",color:"#e2e8f0",pointerEvents:"none",display:"flex",gap:10,alignItems:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.4)"}}>
            <span style={{color:"#64748b"}}>O</span><span>{Number(crosshairOHLC.open).toFixed(2)}</span>
            <span style={{color:"#10b981"}}>H</span><span>{Number(crosshairOHLC.high).toFixed(2)}</span>
            <span style={{color:"#ef4444"}}>L</span><span>{Number(crosshairOHLC.low).toFixed(2)}</span>
            <span style={{color:"#22d3ee"}}>C</span><span style={{fontWeight:700}}>{Number(crosshairOHLC.close).toFixed(2)}</span>
            {crosshairOHLC.volume != null && <><span style={{color:"#64748b",marginLeft:6}}>Vol</span><span>{Number(crosshairOHLC.volume) >= 1000 ? (Number(crosshairOHLC.volume)/1000).toFixed(1)+'k' : Number(crosshairOHLC.volume)}</span></>}
            <span style={{color:"#475569",marginLeft:6}}>{crosshairOHLC.time ? new Date((typeof crosshairOHLC.time === 'number' && crosshairOHLC.time < 1e12 ? crosshairOHLC.time*1000 : crosshairOHLC.time)).toISOString().slice(0,16).replace('T',' ') : ''}</span>
          </div>
        )}

        {/* ── TradingView / MT5 Bar Countdown & Spread Floating HUD ── */}
        <div data-testid="tv-bar-countdown-hud" style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 10,
          background: "rgba(10,24,40,0.92)",
          backdropFilter: "blur(4px)",
          border: "1px solid #1e3a5f",
          borderRadius: 4,
          padding: "3px 8px",
          fontSize: 11,
          fontFamily: "'Courier New', monospace",
          color: "#e2e8f0",
          pointerEvents: "none",
          display: "flex",
          gap: 8,
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.4)"
        }}>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{color:"#64748b"}}>⏳ Bar</span>
            <span style={{fontWeight:700, color: barCountdownSec <= 5 ? "#ef4444" : "#38bdf8"}}>{formatCountdown(barCountdownSec)}</span>
          </span>
          <span style={{color:"#334155"}}>|</span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{color:"#64748b"}}>Spread</span>
            <span style={{fontWeight:600, color:"#f59e0b"}}>{estSpread}</span>
          </span>
        </div>
        <div ref={containerRef} style={{width:"100%",height}}/>

        {/* ── P1 #4 MTF BANNER (default-ON per Kenyon 2026-09-13 voice note) ──
             Sticky top-center strip: shows 4-TF verdict (1m/5m/15m/1h) with bullish/bearish colors,
             overall alignment %, voice summary. User clicks the 🧭 MTF button to hide. */}
        {mtfPanel && (
          <div data-testid="mtf-banner"
               className="kara-mtf-banner"
               style={{
                 position:"absolute",
                 top: isMobile ? 4 : 6,
                 left: "50%",
                 transform: "translateX(-50%)",
                 zIndex: 11,
                 display:"flex",
                 alignItems:"center",
                 gap: isMobile ? 4 : 6,
                 padding: isMobile ? "4px 7px" : "5px 10px",
                 background:"rgba(10,24,40,0.93)",
                 backdropFilter:"blur(8px)",
                 border: (mtfData?.alignment === 'BULLISH' ? "1px solid #10b98155"
                       : mtfData?.alignment === 'BEARISH' ? "1px solid #ef444455"
                       : "1px solid #1e3a5f"),
                 borderRadius: 6,
                 boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
                 fontFamily:"'Courier New', monospace",
                 fontSize: isMobile ? 10 : 11,
                 color: "#e2e8f0",
                 pointerEvents: "auto",
                 maxWidth: isMobile ? "calc(100% - 8px)" : "calc(100% - 16px)",
                 flexWrap: "wrap",
                 justifyContent: "center",
                 rowGap: isMobile ? 4 : 0,
               }}>
              {(() => {
                const label = (mtfData?.symbol || symbol || '').toUpperCase();
                const alignment = mtfData?.alignment || "PENDING";
                const confidence = mtfData?.confidence ?? null;
                const alignedTfs = mtfData?.aligned_tfs || [];
                const tfs = mtfData?.tfs || [];
                // Order: M1, M5, M15, M30, H1 — explicit so missing data still renders in canonical order
                const TF_ORDER = ["1m","5m","15m","30m","1h"];
                const tfByKey = Object.fromEntries(tfs.map(t => [t.tf, t]));
                const orderedTfs = TF_ORDER.map(tf => tfByKey[tf] || { tf, ok:false, verdict:"N/A", error:"no data" });

                const alignmentColor = alignment === 'BULLISH' ? '#10b981'
                                     : alignment === 'BEARISH' ? '#ef4444'
                                     : '#f59e0b';
                const alignmentBg   = alignment === 'BULLISH' ? '#10b98122'
                                     : alignment === 'BEARISH' ? '#ef444422'
                                     : '#f59e0b22';
                // Per-TF color: independent of overall alignment (each TF can be its own verdict)
                const tfPillBg = (v) => v === 'BULLISH' ? '#10b981'
                                  : v === 'BEARISH' ? '#ef4444'
                                  : v === 'NEUTRAL' ? '#f59e0b'
                                  : '#1e3a5f';   // N/A / PENDING / error → dim slate
                const tfPillFg = (v) => (v === 'BULLISH' || v === 'BEARISH' || v === 'NEUTRAL') ? '#000' : '#94a3b8';
                const arrow = (v) => v === 'BULLISH' ? '▲'
                                : v === 'BEARISH' ? '▼'
                                : v === 'NEUTRAL' ? '▬'
                                : '?';
                const tfLabel = (tf) => tf === '1m' ? 'M1' : tf === '5m' ? 'M5' : tf === '15m' ? 'M15' : tf === '30m' ? 'M30' : tf === '1h' ? 'H1' : tf;
                const tfTitle = (t) => {
                  const reasons = (t.reasons || []).slice(0, 4).join(' • ');
                  const s = (t.score == null ? '?' : (t.score > 0 ? '+' + t.score : t.score));
                  const strength = t.strength || (t.score == null ? 'NONE' : Math.abs(t.score) >= 3 ? 'STRONG' : Math.abs(t.score) >= 1 ? 'WEAK' : 'NONE');
                  const priceLine = t.lastPrice != null ? `price ${t.lastPrice.toFixed(2)} • ` : '';
                  return `${tfLabel(t.tf)} • ${t.verdict || 'UNKNOWN'} • score ${s} • ${strength}\n${priceLine}${reasons}${t.error ? '\n⚠ ' + t.error : ''}`;
                };
                // Count how many TFs are aligned with the overall verdict
                const alignedCount = alignedTfs.length;
                const totalCount = orderedTfs.filter(t => t.ok !== false).length || 5;
                const alignedPct = Math.round(100 * alignedCount / totalCount);
                const voiceText = mtfData?.voice || (mtfData ? 'Analyzing…' : 'Loading…');
                return <>
                  {/* Symbol chip — compact on mobile (no border-right) */}
                  <span data-testid="mtf-symbol"
                        style={{fontWeight:700,color:"#22d3ee",letterSpacing:0.5,paddingRight: isMobile ? 5 : 8,borderRight: isMobile ? "none" : "1px solid #1e3a5f",fontSize: isMobile ? 10 : 12, marginRight: isMobile ? 5 : 0}}>
                    {label || '…'}
                  </span>
                  {/* Alignment verdict chip — BIG and obvious (slightly smaller on phones) */}
                  <span data-testid="mtf-alignment"
                        title={voiceText}
                        style={{
                          padding: isMobile ? "3px 7px" : "4px 10px",
                          borderRadius:4,
                          background: alignmentBg,
                          color: alignmentColor,
                          fontWeight:800,
                          letterSpacing: isMobile ? 0.4 : 0.8,
                          fontSize: isMobile ? 11 : 12,
                        }}>
                    {arrow(alignment)} {alignment}
                    {confidence != null && (
                      <span style={{marginLeft: isMobile ? 3 : 5, opacity:0.85, fontWeight:700, fontSize: isMobile ? 10 : 11}}>
                        {confidence}%
                      </span>
                    )}
                  </span>
                  {/* Aligned counter — visible at a glance; compact on phones (3/5 vs 3/5 aligned) */}
                  <span data-testid="mtf-aligned-counter"
                        style={{
                          padding: isMobile ? "3px 6px" : "4px 8px",
                          borderRadius:4,
                          background:"#0f1e2e",
                          color: alignedCount === totalCount ? '#10b981'
                               : alignedCount === 0 ? '#ef4444'
                               : '#f59e0b',
                          fontWeight:700,
                          fontSize: isMobile ? 10 : 11,
                          border:"1px solid #1e3a5f",
                        }}
                        title={`${alignedCount} of ${totalCount} timeframes aligned with ${alignment}`}>
                    {alignedCount}/{totalCount}{isMobile ? "" : " aligned"}
                  </span>
                  {/* TF pills — bigger, arrow + label on top, verdict below */}
                  {orderedTfs.map(t => {
                    const v = t.verdict || (t.ok === false ? 'N/A' : 'PEND');
                    const is30m = t.tf === '30m';
                    return (
                      <span key={t.tf}
                            data-testid={`mtf-tf-${t.tf}`}
                            title={tfTitle(t)}
                            style={{
                              display:"inline-flex",
                              flexDirection:"column",
                              alignItems:"center",
                              padding: isMobile ? "2px 5px" : "4px 9px",
                              minWidth: isMobile ? 34 : 48,
                              borderRadius:4,
                              background: tfPillBg(v),
                              color: tfPillFg(v),
                              fontWeight: 700,
                              border: is30m ? `1px dashed ${tfPillBg(v)}` : `1px solid ${tfPillBg(v)}`,
                              whiteSpace:"nowrap",
                              transition: "background 0.2s, color 0.2s",
                              cursor:"help",
                              lineHeight:1.1,
                            }}>
                        <span style={{fontSize: isMobile ? 9 : 11, letterSpacing:0.4,opacity:0.85}}>
                          {tfLabel(t.tf)} {arrow(v)}
                        </span>
                        <span style={{fontSize: isMobile ? 8 : 10, marginTop:1, letterSpacing:0.3, display: isMobile ? "none" : "inline"}}>
                          {v}
                        </span>
                      </span>
                    );
                  })}
                  {/* Voice summary — visible inline on desktop/tablet, hidden on phones (tooltip intact) */}
                  <span data-testid="mtf-voice"
                        style={{
                          display: isMobile ? "none" : "inline",
                          color:"#cbd5e1",
                          fontSize:10,
                          paddingLeft:8,
                          borderLeft:"1px solid #1e3a5f",
                          maxWidth: 220,
                          whiteSpace:"nowrap",
                          overflow:"hidden",
                          textOverflow:"ellipsis",
                        }}
                        title={voiceText}>
                    🔊 {voiceText}
                  </span>
                </>;
              })()}
          </div>
        )}
        </div>

        {/* Buy/Sell buttons — top-right */}
        {(onBuy || onSell) && (
          <div style={{position:"absolute",top:8,right:8,display:"flex",gap:6,zIndex:10}}>
            {onBuy && (
              <button onClick={()=>{
                const lastCandle = data?.candles?.[data.candles.length-1];
                const entryPrice = lastCandle?.close || null;
                const risk = entryPrice ? Math.max(1, entryPrice * 0.02) : 0; // 2% risk default
                const tp1 = entryPrice ? entryPrice + risk : null;
                const tp2 = entryPrice ? entryPrice + (risk * 2) : null;
                const sl = lastCandle?.low || null;
                setTradeForm({entry:entryPrice, tp1, tp2, sl, stake, duration:"5", duration_unit:"m"});
                setTradeModal({direction:"CALL"});
              }} style={{
                background:"#00d4aa",color:"#000",border:"none",borderRadius:4,
                padding:"6px 14px",fontWeight:700,fontSize:13,cursor:"pointer",
              }}>▲ BUY</button>
            )}
            {onSell && (
              <button onClick={()=>{
                const lastCandle = data?.candles?.[data.candles.length-1];
                const entryPrice = lastCandle?.close || null;
                const risk = entryPrice ? Math.max(1, entryPrice * 0.02) : 0;
                const tp1 = entryPrice ? entryPrice - risk : null;
                const tp2 = entryPrice ? entryPrice - (risk * 2) : null;
                const sl = lastCandle?.high || null;
                setTradeForm({entry:entryPrice, tp1, tp2, sl, stake, duration:"5", duration_unit:"m"});
                setTradeModal({direction:"PUT"});
              }} style={{
                background:"#ef4444",color:"#fff",border:"none",borderRadius:4,
                padding:"6px 14px",fontWeight:700,fontSize:13,cursor:"pointer",
              }}>▼ SELL</button>
            )}
          </div>
        )}

        {/* Active trade badges — bottom-left */}
        {activeTrades.length > 0 && (
          <div style={{position:"absolute",bottom:8,left:8,display:"flex",flexDirection:"column",gap:4,zIndex:10}}>
            {activeTrades.map(t => {
              const isBuy = t.contract_type === "CALL";
              const col = isBuy ? "#00d4aa" : "#ef4444";
              const secsLeft = t.expiry_time
                ? Math.max(0, Math.floor((t.expiry_time * 1000 - Date.now()) / 1000)) : null;
              const expiryLabel = secsLeft != null
                ? secsLeft > 60 ? `${Math.floor(secsLeft/60)}m${secsLeft%60}s` : `${secsLeft}s` : null;
              return (
                <div key={t.contract_id} style={{background:"#0d1f35cc",border:`1px solid ${col}`,
                  borderRadius:4,padding:"4px 8px",fontSize:11,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{color:col,fontWeight:700}}>{isBuy?"▲ BUY":"▼ SELL"}</span>
                  <span style={{color:"#94a3b8"}}>${t.buy_price?.toFixed(2)}</span>
                  {t.pnl != null && (
                    <span style={{color:t.pnl>=0?"#00d4aa":"#ef4444",fontWeight:700}}>
                      {t.pnl>=0?"+":""}{t.pnl.toFixed(2)}
                    </span>
                  )}
                  {expiryLabel && <span style={{color:"#64748b",fontSize:10}}>⏱{expiryLabel}</span>}
                  {onCloseTrade && (
                    <button onClick={()=>onCloseTrade(t.contract_id)} style={{background:"#1e3a5f",
                      color:"#e2e8f0",border:"none",borderRadius:3,padding:"2px 7px",fontSize:11,cursor:"pointer"}}>
                      Exit
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── P1 HOUR HEATMAP — 24-cell UTC hour win-rate grid (top-left) ── */}
        {showHourHeatmap && (
          <div data-testid="hour-heatmap"
               className="kara-hour-heatmap"
               style={{position:"absolute",top:8,left:8,background:"rgba(13,31,53,0.92)",backdropFilter:"blur(6px)",
                       border:"1px solid #1e3a5f",borderRadius:6,padding:6,zIndex:9,
                       boxShadow:"0 4px 16px rgba(0,0,0,0.5)",fontFamily:"'Courier New',monospace",maxWidth:"calc(100vw - 24px)",overflowX:"auto",overflowY:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,whiteSpace:"nowrap"}}>
              <span style={{fontSize:10,color:"#94a3b8",letterSpacing:1,fontWeight:700}}>🕐 UTC HOUR HEAT</span>
              <span style={{fontSize:9,color:"#64748b"}}>(wr%)</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(24, 18px)",gap:1,minWidth:0}}>
              {Array.from({length:24}, (_,h)=>{
                const stat = hourlyStats[h];
                const wr = stat && stat.trades >= 3 ? stat.win_rate_pct : null;
                // color: red <45, yellow 45-55, green 55-65, hot-green >65, dim if no data
                const bg = wr == null ? "#1e3a5f44"
                  : wr < 45 ? "#ef4444"
                  : wr < 55 ? "#f59e0b"
                  : wr < 65 ? "#00d4aa"
                  : "#10b981";
                const fg = wr != null ? "#000" : "#475569";
                const title = stat
                  ? `Hour ${String(h).padStart(2,'0')}:00 UTC — ${stat.trades} trades, ${stat.wins}W/${stat.losses}L, WR ${wr != null ? wr.toFixed(1) : 'n/a'}%, PnL $${stat.pnl.toFixed(2)}`
                  : `Hour ${String(h).padStart(2,'0')}:00 UTC — no trade history`;
                return (
                  <div key={h}
                       onClick={()=>{
                         // jump chart to this hour's candles
                         try {
                           const ts = chartRef.current;
                           if (!ts) return;
                           const candles = getSorted();
                           if (!candles.length) return;
                           // Find the candle closest to h:00 of the most recent day
                           const now = new Date();
                           const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, 0, 0));
                           const targetSec = Math.floor(target.getTime()/1000);
                           // If target hour is in the future today, go back one day
                           if (targetSec > candles[candles.length-1].time) target.setUTCDate(target.getUTCDate() - 1);
                           const targetSecAdj = Math.floor(target.getTime()/1000);
                           ts.timeScale().setVisibleRange({ from: targetSecAdj - 3600, to: targetSecAdj + 3600 * 4 });
                         } catch {}
                       }}
                       onMouseEnter={()=>setHoveredHour(h)}
                       onMouseLeave={()=>setHoveredHour(null)}
                       title={title}
                       style={{width:18,height:22,background:bg,color:fg,fontSize:9,fontWeight:700,
                               display:"flex",alignItems:"center",justifyContent:"center",
                               cursor:"pointer",border: hoveredHour===h?"1px solid #e2e8f0":"1px solid transparent",
                               borderRadius:2,transition:"all 0.1s"}}>
                    {h}
                  </div>
                );
              })}
            </div>
            {hoveredHour != null && hourlyStats[hoveredHour] && (
              <div style={{marginTop:4,padding:"4px 6px",background:"#0a1628",borderRadius:3,fontSize:10,color:"#94a3b8"}}>
                <span style={{color:"#e2e8f0",fontWeight:700}}>{String(hoveredHour).padStart(2,'0')}:00 UTC</span>
                {" — "}
                <span style={{color: hourlyStats[hoveredHour].win_rate_pct >= 55 ? "#00d4aa" : hourlyStats[hoveredHour].win_rate_pct >= 45 ? "#f59e0b" : "#ef4444"}}>
                  {hourlyStats[hoveredHour].win_rate_pct.toFixed(1)}% WR
                </span>
                {" • "}{hourlyStats[hoveredHour].trades} trades • ${hourlyStats[hoveredHour].pnl.toFixed(2)}
              </div>
            )}
            {/* Legend */}
            <div style={{display:"flex",gap:4,marginTop:4,fontSize:9,color:"#64748b",alignItems:"center"}}>
              <span style={{width:10,height:10,background:"#ef4444",borderRadius:2}}/>
              <span>&lt;45</span>
              <span style={{width:10,height:10,background:"#f59e0b",borderRadius:2,marginLeft:6}}/>
              <span>45-55</span>
              <span style={{width:10,height:10,background:"#00d4aa",borderRadius:2,marginLeft:6}}/>
              <span>55-65</span>
              <span style={{width:10,height:10,background:"#10b981",borderRadius:2,marginLeft:6}}/>
              <span>65+</span>
            </div>
          </div>
        )}

        {/* ML Prediction Overlay */}
        {mlPredictionOverlay && (
          <div style={{position:"absolute",top:60,right:12,width:280,background:"rgba(10, 22, 40, 0.95)",backdropFilter:"blur(8px)",border:"1px solid #1e3a5f",borderRadius:8,padding:16,zIndex:20,boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}>
            <button onClick={() => setMlPredictionOverlay(null)} style={{position:"absolute",top:8,right:8,background:"transparent",border:"none",color:"#64748b",cursor:"pointer"}}>✕</button>
            <h3 style={{fontSize:12,color:"#a855f7",margin:"0 0 10px 0",display:"flex",alignItems:"center",gap:6}}>
              {"\uD83E\uDD16"} ML Prediction Model
            </h3>
            {mlPredictionOverlay.loading ? (
              <div style={{fontSize:12,color:"#94a3b8"}}>Analyzing real-time model vectors...</div>
            ) : mlPredictionOverlay.error ? (
              <div style={{fontSize:12,color:"#ef4444"}}>{mlPredictionOverlay.error}</div>
            ) : (
              <div style={{fontSize:12,color:"#e2e8f0"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span>Prediction:</span> <strong style={{color:mlPredictionOverlay.prediction==="CALL"?"#00d4aa":mlPredictionOverlay.prediction==="PUT"?"#ef4444":"#94a3b8"}}>{mlPredictionOverlay.prediction}</strong></div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}><span>Confidence:</span> <strong>{((mlPredictionOverlay.confidence || mlPredictionOverlay.win_probability || 0)*100).toFixed(1)}%</strong></div>
                {mlPredictionOverlay.probabilities && (
                  <div style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#64748b"}}>
                      <span>CALL: {(mlPredictionOverlay.probabilities.CALL*100).toFixed(0)}%</span>
                      <span>PUT: {(mlPredictionOverlay.probabilities.PUT*100).toFixed(0)}%</span>
                    </div>
                    <div style={{width:"100%",height:4,background:"#1e3a5f",borderRadius:2,marginTop:2,overflow:"hidden",display:"flex"}}>
                      <div style={{height:"100%",background:"#00d4aa",width:`${mlPredictionOverlay.probabilities.CALL*100}%`}}></div>
                      <div style={{height:"100%",background:"#ef4444",width:`${mlPredictionOverlay.probabilities.PUT*100}%`}}></div>
                    </div>
                  </div>
                )}
                
                {/* Trade Execution Buttons */}
                <div style={{display:"flex",gap:6,marginTop:12}}>
                  {onBuy && mlPredictionOverlay.prediction === "CALL" && (
                    <button onClick={() => {
                      const lastCandle = data?.candles?.[data.candles.length-1];
                      const entryPrice = lastCandle?.close || null;
                      const confidence = mlPredictionOverlay.confidence || mlPredictionOverlay.win_probability || 0;
                      const risk = entryPrice ? Math.max(1, entryPrice * 0.01 * confidence) : 0; // Risk based on confidence
                      const tp1 = entryPrice ? entryPrice + risk * 2 : null; // 2:1 reward
                      const sl = entryPrice ? entryPrice - risk : null;
                      setTradeForm({entry:entryPrice, tp1, tp2:null, sl, stake, duration:"5", duration_unit:"m"});
                      setTradeModal({direction:"CALL"});
                      setMlPredictionOverlay(null);
                    }} style={{
                      flex:1, background:"#00d4aa",color:"#000",border:"none",borderRadius:4,
                      padding:"8px 12px",fontWeight:700,fontSize:12,cursor:"pointer",
                    }}>📈 EXECUTE BUY</button>
                  )}
                  {onSell && mlPredictionOverlay.prediction === "PUT" && (
                    <button onClick={() => {
                      const lastCandle = data?.candles?.[data.candles.length-1];
                      const entryPrice = lastCandle?.close || null;
                      const confidence = mlPredictionOverlay.confidence || mlPredictionOverlay.win_probability || 0;
                      const risk = entryPrice ? Math.max(1, entryPrice * 0.01 * confidence) : 0;
                      const tp1 = entryPrice ? entryPrice - risk * 2 : null;
                      const sl = entryPrice ? entryPrice + risk : null;
                      setTradeForm({entry:entryPrice, tp1, tp2:null, sl, stake, duration:"5", duration_unit:"m"});
                      setTradeModal({direction:"PUT"});
                      setMlPredictionOverlay(null);
                    }} style={{
                      flex:1, background:"#ef4444",color:"#fff",border:"none",borderRadius:4,
                      padding:"8px 12px",fontWeight:700,fontSize:12,cursor:"pointer",
                    }}>📉 EXECUTE SELL</button>
                  )}
                </div>
                
                {mlPredictionOverlay.strategyEvaluation && mlPredictionOverlay.strategyEvaluation.length > 0 && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:10,color:"#94a3b8",marginBottom:4}}>Model Inference Flags:</div>
                    {mlPredictionOverlay.strategyEvaluation.map((rule, idx) => (
                      <div key={idx} style={{fontSize:10,color:"#64748b",display:"flex",alignItems:"center",gap:4}}>
                        <span style={{color:rule.passed?"#00d4aa":"#ef4444"}}>{rule.passed?"✓":"✕"}</span> {rule.condition}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sub-pane ── */}
      {hasSubPane && (
        <div style={{borderTop:"1px solid #1e293b"}}>
          <div style={{fontSize:9,color:"#334155",padding:"2px 10px",background:"#070d1a",display:"flex",gap:8}}>
            {Object.keys(activeInds).filter(id=>INDICATOR_CATALOGUE.find(i=>i.id===id)?.pane==="sub").map(id=>{
              const def=INDICATOR_CATALOGUE.find(i=>i.id===id); const color=activeInds[id]?.color||def.color;
              return <span key={id} style={{color}}>{def.label}</span>;
            })}
          </div>
          <div className="sub-pane" style={{width:"100%",height:110}}/>
        </div>
      )}
    

      {showColorPicker && (
        <div style={{position:'absolute',top:100,right:10,background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:8,padding:16,zIndex:1000,minWidth:200,boxShadow:'0 4px 6px rgba(0,0,0,0.3)'}}>
          <h4 style={{margin:'0 0 12px 0',color:'#e2e8f0',fontSize:12,fontWeight:700}}>Candlestick Colors</h4>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <label style={{color:'#94a3b8',fontSize:11,flex:1}}>Up:</label>
              <input type="color" value={candleColors.up} onChange={e => setCandleColors({...candleColors,up:e.target.value})} style={{width:40,height:28,border:'none',borderRadius:4,cursor:'pointer'}}/>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <label style={{color:'#94a3b8',fontSize:11,flex:1}}>Down:</label>
              <input type="color" value={candleColors.down} onChange={e => setCandleColors({...candleColors,down:e.target.value})} style={{width:40,height:28,border:'none',borderRadius:4,cursor:'pointer'}}/>
            </div>
            <button onClick={() => {setCandleColors({up:'#00d4aa',down:'#ef4444',wick:'#94a3b8'}); setShowColorPicker(false);}} style={{padding:'6px 12px',background:'#1e3a5f',color:'#94a3b8',border:'1px solid #475569',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:600}}>Reset</button>
          </div>
        </div>
      )}

      {/* Price Alerts Modal */}
      {alertModal && (
        <div style={{position:'absolute',top:100,left:10,background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:8,padding:16,zIndex:1000,minWidth:300,boxShadow:'0 4px 6px rgba(0,0,0,0.3)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h4 style={{margin:0,color:'#e2e8f0',fontSize:12,fontWeight:700}}>🔔 Price Alerts</h4>
            <button onClick={() => setAlertModal(null)} style={{background:'transparent',border:'none',color:'#64748b',cursor:'pointer',fontSize:16}}>×</button>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {priceAlerts.map((alert, idx) => (
              <div key={idx} style={{display:'flex',alignItems:'center',gap:8,padding:'6px',background:'#1e3a5f',borderRadius:4}}>
                <span style={{color:alert.condition === 'above' ? '#00d4aa' : '#ef4444',fontSize:11}}>
                  {alert.condition === 'above' ? '↑' : '↓'}
                </span>
                <span style={{color:'#e2e8f0',fontSize:11}}>${alert.price.toFixed(2)}</span>
                <span style={{color:'#94a3b8',fontSize:10,flex:1}}>{alert.message}</span>
                <button onClick={() => setPriceAlerts(prev => prev.filter((_, i) => i !== idx))} 
                  style={{background:'transparent',border:'none',color:'#ef4444',cursor:'pointer',fontSize:12}}>×</button>
              </div>
            ))}
            <div style={{display:'flex',gap:6,marginTop:8}}>
              <select style={{flex:1,padding:'6px',background:'#1e3a5f',border:'1px solid #475569',borderRadius:4,color:'#e2e8f0',fontSize:11}}>
                <option value="above">Above</option>
                <option value="below">Below</option>
              </select>
              <input type="number" placeholder="Price" step="0.01" 
                style={{width:80,padding:'6px',background:'#1e3a5f',border:'1px solid #475569',borderRadius:4,color:'#e2e8f0',fontSize:11}}/>
              <input type="text" placeholder="Message" 
                style={{flex:1,padding:'6px',background:'#1e3a5f',border:'1px solid #475569',borderRadius:4,color:'#e2e8f0',fontSize:11}}/>
              <button style={{padding:'6px 12px',background:'#00d4aa',color:'#000',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:600}}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Drawing Tools Modal */}
      {drawingModal && (
        <div style={{position:'absolute',top:100,left:320,background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:8,padding:16,zIndex:1000,minWidth:260,boxShadow:'0 4px 24px rgba(0,0,0,0.5)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h4 style={{margin:0,color:'#e2e8f0',fontSize:12,fontWeight:700}}>✏️ Drawing Tools</h4>
            <button onClick={() => setDrawingModal(false)} style={{background:'transparent',border:'none',color:'#64748b',cursor:'pointer',fontSize:16}}>×</button>
          </div>
          {drawingAnchor && <div style={{fontSize:11,color:'#f59e0b',marginBottom:8,padding:'4px 8px',background:'#1a1a00',borderRadius:4}}>📍 Click chart to set end point…</div>}
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {[
              ['trendline','📈 Trend Line','#3b82f6','click twice'],
              ['ray','↗️ Extended Ray Line','#38bdf8','click twice'],
              ['horizontal','↔️ Horizontal Level','#f59e0b','click once'],
              ['vertical','↕️ Vertical Time Marker','#eab308','click once'],
              ['fibonacci','📏 Fibonacci Retracement','#a855f7','click twice'],
              ['rectangle','▭ Rectangle Zone','#22d3ee','click twice']
            ].map(([type, label, color, hint]) => (
              <button key={type}
                onClick={() => { setDrawingMode(m => m === type ? null : type); setDrawingAnchor(null); }}
                style={{padding:'9px 12px',background:drawingMode===type?color+'33':'#1e3a5f',color:drawingMode===type?color:'#e2e8f0',border:`1px solid ${drawingMode===type?color:'#475569'}`,borderRadius:4,cursor:'pointer',fontSize:11,textAlign:'left',fontWeight:drawingMode===type?700:400}}>
                {label} {drawingMode===type ? `✓ ACTIVE (${hint})` : ''}
              </button>
            ))}
            <div style={{borderTop:'1px solid #1e3a5f',marginTop:4,paddingTop:8,display:'flex',gap:6}}>
              <button onClick={() => { setDrawingMode(null); setDrawingAnchor(null); }}
                style={{flex:1,padding:'6px',background:'#0f1c2e',color:'#94a3b8',border:'1px solid #475569',borderRadius:4,cursor:'pointer',fontSize:11}}>
                Stop Drawing
              </button>
              <button onClick={() => { setDrawings([]); setDrawingMode(null); setDrawingAnchor(null); }}
                style={{flex:1,padding:'6px',background:'#ef444422',color:'#ef4444',border:'1px solid #ef444444',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:700}}>
                🗑️ Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P1 #6 Layout Presets Modal — save / load named chart setups */}
      {presetModal && (
        <div style={{position:'absolute',top:100,left:600,background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:8,padding:16,zIndex:1000,minWidth:300,maxHeight:480,overflowY:'auto',boxShadow:'0 4px 24px rgba(0,0,0,0.5)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h4 style={{margin:0,color:'#e2e8f0',fontSize:12,fontWeight:700}}>💾 Chart Setup Presets</h4>
            <button onClick={() => setPresetModal(null)} style={{background:'transparent',border:'none',color:'#64748b',cursor:'pointer',fontSize:16}}>×</button>
          </div>
          <p style={{margin:'0 0 10px',fontSize:11,color:'#94a3b8'}}>
            Capture every indicator, drawing, overlay & candle color as a named setup.
            Voice: <code style={{color:'#10b981'}}>save setup scalper-1</code> · <code style={{color:'#10b981'}}>load scalper-1</code>
          </p>

          {/* Save section */}
          <div style={{marginBottom:14,padding:10,background:'#0a1828',borderRadius:6,border:'1px solid #1e3a5f'}}>
            <div style={{fontSize:11,color:'#94a3b8',marginBottom:6,fontWeight:600}}>📥 SAVE CURRENT SETUP</div>
            <div style={{display:'flex',gap:6}}>
              <input
                value={presetName}
                onChange={e => setPresetName(e.target.value.replace(/[^a-zA-Z0-9 _\-]/g, ''))}
                placeholder="preset name (e.g. scalper-1)"
                style={{flex:1,padding:'6px 8px',background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:4,color:'#e2e8f0',fontSize:12,fontFamily:'inherit'}}
                onKeyDown={e => { if (e.key === 'Enter') {
                  const name = presetName.trim().replace(/\s+/g, '-').toLowerCase();
                  if (!name) return;
                  const layout = {
                    v: 1, ts: Date.now(), symbol, timeframe,
                    activeInds, customInds, drawings, candleColors,
                    overlays: { showSessions, showHourHeatmap, showHistoryMarkers, showActiveMarkers, showStrategyInd },
                  };
                  try {
                    localStorage.setItem('kara_chart_preset_' + name, JSON.stringify(layout));
                    setPresetName('');
                    refreshPresetList();
                    addLog?.(`✅ Saved setup: ${name}`);
                  } catch (err) { addLog?.(`❌ Save failed: ${err.message}`); }
                } }}
              />
              <button
                onClick={() => {
                  const name = presetName.trim().replace(/\s+/g, '-').toLowerCase();
                  if (!name) { addLog?.('⚠️ Enter a preset name first.'); return; }
                  const layout = {
                    v: 1, ts: Date.now(), symbol, timeframe,
                    activeInds, customInds, drawings, candleColors,
                    overlays: { showSessions, showHourHeatmap, showHistoryMarkers, showActiveMarkers, showStrategyInd },
                  };
                  try {
                    localStorage.setItem('kara_chart_preset_' + name, JSON.stringify(layout));
                    setPresetName('');
                    refreshPresetList();
                    addLog?.(`✅ Saved setup: ${name}`);
                  } catch (err) { addLog?.(`❌ Save failed: ${err.message}`); }
                }}
                style={{padding:'6px 12px',background:'#10b981',color:'#0a1828',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:700}}>
                Save
              </button>
            </div>
          </div>

          {/* Load list */}
          <div>
            <div style={{fontSize:11,color:'#94a3b8',marginBottom:6,fontWeight:600}}>📂 LOAD SETUP ({presetList.length})</div>
            {presetList.length === 0 && (
              <div style={{padding:10,fontSize:11,color:'#64748b',textAlign:'center',fontStyle:'italic'}}>
                No presets saved yet. Configure the chart and click Save.
              </div>
            )}
            {presetList.map(name => {
              let meta = null;
              try {
                const raw = localStorage.getItem('kara_chart_preset_' + name);
                if (raw) meta = JSON.parse(raw);
              } catch {}
              return (
                <div key={name} style={{display:'flex',alignItems:'center',gap:6,padding:8,marginBottom:4,background:'#0a1828',borderRadius:4,border:'1px solid #1e3a5f'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,color:'#e2e8f0',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</div>
                    <div style={{fontSize:10,color:'#64748b'}}>
                      {meta ? `${meta.symbol}/${meta.timeframe} · ${Object.keys(meta.activeInds||{}).length} indicators · ${(meta.drawings||[]).length} drawings · ${meta.ts ? new Date(meta.ts).toLocaleDateString() : '?'}` : 'corrupted'}
                    </div>
                  </div>
                  <button onClick={() => {
                    try {
                      const raw = localStorage.getItem('kara_chart_preset_' + name);
                      if (!raw) return;
                      const layout = JSON.parse(raw);
                      // Mirror applyLayout
                      if (layout.activeInds) setActiveInds(layout.activeInds);
                      if (layout.customInds) setCustomInds(layout.customInds);
                      if (layout.drawings) setDrawings(layout.drawings);
                      if (layout.candleColors) setCandleColors(layout.candleColors);
                      const ov = layout.overlays || {};
                      if ('showSessions' in ov) setShowSessions(ov.showSessions);
                      if ('showHourHeatmap' in ov) setShowHourHeatmap(ov.showHourHeatmap);
                      if ('showHistoryMarkers' in ov) setShowHistoryMarkers(ov.showHistoryMarkers);
                      if ('showActiveMarkers' in ov) setShowActiveMarkers(ov.showActiveMarkers);
                      if ('showStrategyInd' in ov) setShowStrategyInd(ov.showStrategyInd);
                      setPresetModal(null);
                      addLog?.(`✅ Loaded setup: ${name}`);
                    } catch (err) { addLog?.(`Load failed: ${err.message}`); }
                  }} style={{padding:'4px 10px',background:'#3b82f6',color:'#fff',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:600}}>
                    Load
                  </button>
                  <button onClick={() => {
                    if (window.confirm(`Delete preset "${name}"?`)) {
                      try { localStorage.removeItem('kara_chart_preset_' + name); refreshPresetList(); } catch {}
                    }
                  }} style={{padding:'4px 8px',background:'#7f1d1d',color:'#fca5a5',border:'none',borderRadius:4,cursor:'pointer',fontSize:11}}>
                    🗑️
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* P1 #5 Per-Symbol Risk Score Modal — composite 0-100 score from trades.db */}
      {riskModal && (
        <div style={{position:'absolute',top:100,left:920,background:'#0f1c2e',border:'1px solid #1e3a5f',borderRadius:8,padding:16,zIndex:1000,minWidth:340,maxWidth:520,maxHeight:480,overflowY:'auto',boxShadow:'0 4px 24px rgba(0,0,0,0.5)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h4 style={{margin:0,color:'#e2e8f0',fontSize:12,fontWeight:700}}>Per-Symbol Risk Score</h4>
            <button onClick={() => setRiskModal(false)} style={{background:'transparent',border:'none',color:'#64748b',cursor:'pointer',fontSize:16}}>×</button>
          </div>
          <p style={{margin:'0 0 10px',fontSize:11,color:'#94a3b8'}}>
            Composite score (0–100) from live trades. Higher = safer to trade.
            Voice: <code style={{color:'#ef4444'}}>how risky is R_25?</code>
          </p>
          {riskLoading && <div style={{padding:14,fontSize:12,color:'#64748b',textAlign:'center'}}>⏳ Loading…</div>}
          {!riskLoading && !riskData && (
            <div style={{padding:14,fontSize:12,color:'#64748b',textAlign:'center'}}>
              No data. Click Refresh.
            </div>
          )}
          {!riskLoading && riskData && riskData.risk && (
            <div>{/* single-symbol */}<RiskRow r={riskData.risk} /></div>
          )}
          {!riskLoading && riskData && riskData.risks && (
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {riskData.risks.map(r => <RiskRow key={r.symbol} r={r} />)}
            </div>
          )}
          <div style={{marginTop:12,display:'flex',gap:6}}>
            <button onClick={() => fetchRiskScores()} style={{flex:1,padding:'6px 12px',background:'#0f1c2e',color:'#e2e8f0',border:'1px solid #1e3a5f',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:600}}>
              🔄 Refresh
            </button>
            <button onClick={() => fetchRiskScores(symbol)} style={{flex:1,padding:'6px 12px',background:'#3b82f6',color:'#fff',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:600}}>
              🎯 {symbol}
            </button>
          </div>
        </div>
      )}
</div>
  );
});

// RiskRow — single line in the risk panel (used by both single-symbol + ranked views)
const RiskRow = function({ r }) {
  const gradeColor = { A: '#10b981', B: '#22d3ee', C: '#f59e0b', D: '#f97316', F: '#ef4444' }[r.grade] || '#94a3b8';
  const riskColor  = { LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#f97316', EXTREME: '#ef4444' }[r.risk_label] || '#94a3b8';
  const be = r.score.breakeven_wr != null ? `${r.score.breakeven_wr}%` : '—';
  const edge = r.score.edge_vs_be != null ? `${r.score.edge_vs_be >= 0 ? '+' : ''}${r.score.edge_vs_be}pp` : '—';
  return (
    <div style={{padding:'8px 10px',background:'#0a1828',border:`1px solid ${gradeColor}44`,borderRadius:6,marginBottom:6}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:13,color:'#e2e8f0',fontWeight:700,fontFamily:'monospace'}}>{r.symbol}</span>
          <span style={{fontSize:10,padding:'1px 6px',borderRadius:3,background:gradeColor+'22',color:gradeColor,fontWeight:700}}>GRADE {r.grade}</span>
          <span style={{fontSize:10,padding:'1px 6px',borderRadius:3,background:riskColor+'22',color:riskColor,fontWeight:700}}>{r.risk_label}</span>
          {r.score.contract_type === 'multiplier' && <span style={{fontSize:9,color:'#64748b'}}>× mult</span>}
        </div>
        <div style={{fontSize:16,color:gradeColor,fontWeight:700,fontFamily:'monospace'}}>{r.score.total}<span style={{fontSize:10,color:'#64748b'}}>/100</span></div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginTop:6,fontSize:10,color:'#94a3b8'}}>
        <div><span style={{color:'#64748b'}}>WR:</span> <span style={{color:'#e2e8f0',fontWeight:600}}>{r.wr_pct}%</span></div>
        <div><span style={{color:'#64748b'}}>BE:</span> <span style={{color:'#e2e8f0'}}>{be}</span></div>
        <div><span style={{color:'#64748b'}}>Edge:</span> <span style={{color:'#e2e8f0'}}>{edge}</span></div>
        <div><span style={{color:'#64748b'}}>N:</span> <span style={{color:'#e2e8f0'}}>{r.trades}</span></div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginTop:2,fontSize:10,color:'#94a3b8'}}>
        <div><span style={{color:'#64748b'}}>PnL:</span> <span style={{color: r.pnl_total >= 0 ? '#10b981' : '#ef4444',fontWeight:600}}>{r.pnl_total >= 0 ? '+' : ''}${r.pnl_total.toFixed(2)}</span></div>
        <div><span style={{color:'#64748b'}}>$/tr:</span> <span style={{color:'#e2e8f0'}}>${r.pnl_per_trade.toFixed(2)}</span></div>
        {r.best_hour  && <div><span style={{color:'#64748b'}}>Best:</span> <span style={{color:'#10b981'}}>H{r.best_hour.hour} ({r.best_hour.wr}%)</span></div>}
        {r.worst_hour && <div><span style={{color:'#64748b'}}>Worst:</span> <span style={{color:'#ef4444'}}>H{r.worst_hour.hour} ({r.worst_hour.wr}%)</span></div>}
      </div>
    </div>
  );
}


export default ChartWidget;

const btnS = { padding:"3px 9px",borderRadius:4,fontSize:13,fontWeight:700,background:"#0f1c2e",border:"1px solid #1e3a5f",color:"#64748b",cursor:"pointer",fontFamily:"inherit",lineHeight:1 };
